// CompanyView.jsx
// ============================================================================
// The "Company" business-home hub for a project (opened from the sidebar).
//
// Visual language is COPIED from shimmer-web/public/aria-ceo-apple.html
// (the locked Apple-styled CEO panel) — do not redesign it. We keep OUR
// info, dressed in that look:
//   • Title       = the business name, Mac gradient hero (.pt-h).
//   • Top bar     = green orb + Night/Day theme toggle + Week/Month/Year pills.
//   • KPI bubbles = whatever matters for the business type (Aria classifies),
//                   rendered as the Mac glass bubbles.
//   • Money row   = Gross · Expenses · Net (same glass bubbles).
//   • Aria's Work = the agents we run, rendered as the cyan "Today's tasks"
//                   cards (numbered badge + cadence pill).
// ============================================================================

import React from 'react';
import { getApiBase } from './apiBase';
import { renderEmph } from './ariaBoxKit';
import OnboardingView from './OnboardingView';
import { createPortal as _createPortal } from 'react-dom';
import { getChannel, loadManifest } from './releaseChannel'; // release-channel UI gating
import SetupHelpBox from './SetupHelpBox'; // support-chat "Need some help?" popup on Set up
import ScheduleBoard from './agentPanels/ScheduleBoard'; // full-screen week grid + workers + map
// NewJobButton import removed with the button itself (8/13) — an unused import
// still ships in the bundle.
import InvoicePreview from './InvoicePreview'; // pro invoice "blueprint" shown in Invoicing setup
import LeadsPanel from './agentPanels/LeadsPanel';
import QuotesPanel from './agentPanels/QuotesPanel';
import InvoicingPanel from './agentPanels/InvoicingPanel';
import PaymentsPanel from './agentPanels/PaymentsPanel';
import FollowupsPanel from './agentPanels/FollowupsPanel';
import ExpensesPanel from './agentPanels/ExpensesPanel';
import TaxesPanel from './agentPanels/TaxesPanel';
import ReviewsPanel from './agentPanels/ReviewsPanel';
import LicensingPanel from './agentPanels/LicensingPanel';

// Company-page agent key → release-manifest automation key (GET /api/release/
// manifest → automations). On the 'release' channel only automations whose key
// is true are shown; anything UNMAPPED hides too (only proven features ship).
const AGENT_RELEASE_KEY = {
  scheduling: 'schedule',
  leads: 'leads',
  expenses: 'cabinet', // books/receipts/statements live in the File Cabinet
  quotes: 'payments', invoicing: 'payments', payments: 'payments',
  taxes: 'licensing', licensing: 'licensing',
  followups: 'promotions', reviews: 'promotions',
};

// key → its custom detail panel (Scheduling has its own component handled separately)
const AGENT_PANELS = {
  leads: LeadsPanel, quotes: QuotesPanel, invoicing: InvoicingPanel, payments: PaymentsPanel,
  followups: FollowupsPanel, expenses: ExpensesPanel, taxes: TaxesPanel, reviews: ReviewsPanel, licensing: LicensingPanel,
};

function archetypeOf(...hints) {
  const t = hints.map((h) => String(h || '')).join(' ').toLowerCase();
  if (/saas|software|\bapp\b|tech|subscription|platform/.test(t)) return 'saas';
  if (/junk|plumb|hvac|heat|cool|electric|landscap|clean|roof|paint|contract|construct|haul|repair|\bauto\b|service|mechanic/.test(t)) return 'trades';
  if (/shop|store|retail|ecommerce|e-commerce|goods|\bproduct/.test(t)) return 'retail';
  if (/memoir|book|novel|writ|\bart\b|music|design|photo|creative/.test(t)) return 'creative';
  if (/campaign|marketing|launch|\bads?\b|promo/.test(t)) return 'campaign';
  if (/website|portfolio|personal|blog/.test(t)) return 'web';
  return 'default';
}

// TOP — the KPIs that matter for the business type (Aria's classification picks).
// TOP — WHICH KPIs a business type shows. LABELS ONLY, never values.
//
// 8/22: every entry here used to carry a number — '$8,400' MRR, '142' active
// users, '$12,800' revenue — and the workstation printed them as if they were
// this owner's figures. Nothing computed them. Opening any non-trades project
// showed a business performing well that did not exist.
//
// The trades branch already did this right: it reads /api/company/financials
// and prints money() which renders '—' when there is no data. Same rule now
// applies everywhere — the label says what we would show, the value stays '—'
// until something real fills it. A blank is honest; a placeholder is a lie the
// user cannot tell from a fact.
const TOP = {
  saas: [
    { label: 'Recurring revenue (MRR)', tone: 'neutral' },
    { label: 'Active users', tone: 'neutral' },
    { label: 'New signups', tone: 'neutral' },
  ],
  trades: [
    { label: 'Revenue', tone: 'neutral' },
    { label: 'Jobs', tone: 'neutral' },
    { label: 'Unpaid / overdue', tone: 'neutral' },
  ],
  services: [
    { label: 'Revenue', tone: 'neutral' },
    { label: 'Appointments', tone: 'neutral' },
    { label: 'No-shows', tone: 'neutral' },
  ],
  retail: [
    { label: 'Sales', tone: 'neutral' },
    { label: 'Orders', tone: 'neutral' },
    { label: 'Avg order', tone: 'neutral' },
  ],
  creative: [
    { label: 'Progress', tone: 'neutral' },
    { label: 'Open commissions', tone: 'neutral' },
    { label: 'Earnings', tone: 'neutral' },
  ],
  campaign: [
    { label: 'Spend', tone: 'neutral' },
    { label: 'Leads generated', tone: 'neutral' },
    { label: 'Cost per lead', tone: 'neutral' },
  ],
  web: [
    { label: 'Visitors', tone: 'neutral' },
    { label: 'Pages live', tone: 'neutral' },
    { label: 'Contact leads', tone: 'neutral' },
  ],
  default: [
    { label: 'Revenue', tone: 'neutral' },
    { label: 'Pending invoices', tone: 'neutral' },
  ],
};

// BOTTOM deleted 8/22 — it was declared, never rendered, and held a second set
// of invented money ('Gross $12,800', 'Net $9,860'). The real money row is
// bottomMetrics, built from /api/company/financials.

const AGENT_GROUPS = [
  {
    group: "Aria's Work",
    big: true,
    items: [
      { label: 'Scheduling', hint: 'Books jobs & sends reminders', cadence: 'Daily', go: 'https://calendar.google.com/calendar/u/0/r', ext: true },
      { label: 'Leads', hint: 'Captures & follows up', cadence: 'Daily', go: 'leads' },
      { label: 'Invoicing', hint: 'Sends invoices, chases overdue', cadence: 'Per job' },
      { label: 'Quotes & estimates', hint: 'Builds & sends', cadence: 'Per job' },
      { label: 'Expenses', hint: 'Logs receipts & spend', cadence: 'Weekly' },
      { label: 'Customer follow-up', hint: 'Replies, reminders, keeps records', cadence: 'Daily' },
      { label: 'Payments', hint: 'Collects by card & bank transfer', cadence: 'Per job' },
    ],
  },
  {
    group: 'Throughout the year',
    big: false,
    items: [
      { label: 'Reviews', hint: 'Asks happy customers, replies to all', cadence: 'Monthly' },
      { label: 'Bookkeeping', hint: 'Reconciles, categorizes, monthly P&L', cadence: 'Monthly' },
      { label: 'Taxes', hint: 'Sets aside %, files quarterly & year-end', cadence: 'Quarterly' },
      { label: 'Licensing & insurance', hint: 'Tracks renewals before they lapse', cadence: 'Yearly' },
    ],
  },
];

// The 24/7 work agents on the Company home. Title-only on the board; tap →
// AgentDetail (description + what it does + Set up). Not set up = "See more" +
// "Set up"; set up = blue/active. Each agent's specific controls get wired in one
// by one — this is the shared framework + every agent's plain description.
const AGENTS = [
  { key: 'scheduling', group: 'work', title: 'Scheduling', cadence: 'Daily',
    desc: 'Books jobs on your Google Calendar and texts customers a reminder before each appointment — your calendar stays exactly as it is, just linked here.',
    includes: ['Your live Google Calendar, linked in', 'Automatic job reminders by text'] },
  { key: 'leads', group: 'work', title: 'Leads', cadence: 'Daily',
    desc: 'Pulls every lead into one place and shows how many are waiting — by source: Instagram, Facebook, Google, your website, referrals, wherever they come from.',
    includes: ['Your active lead sources, in one list', 'A live count of leads in each'] },
  { key: 'quotes', group: 'work', title: 'Quotes & estimates', cadence: 'Per job',
    desc: 'Every job you scoped on the phone — title, photos, what needs doing — turned into a priced estimate you can edit before it goes out, because the AI isn’t always perfect.',
    includes: ['Each job with its photos', 'Editable AI estimates'] },
  { key: 'invoicing', group: 'work', title: 'Invoicing', cadence: 'Per job',
    desc: 'When a job is done, Aria drafts the invoice and — once you’ve looked it over — sends it by email and text, for a full payment or financing.',
    includes: ['Auto-drafted on completion', 'Sent by email + text'] },
  { key: 'payments', group: 'work', title: 'Payments', cadence: 'Per job',
    desc: 'You review the invoice, tap Send, and the customer gets a pay link — pay in full or finance it. Card, bank, Apple/Google Pay, Cash App, and Affirm/Klarna/Afterpay.',
    includes: ['You review, then Send', 'Full amount or financing'] },
  { key: 'followups', group: 'work', title: 'Customer follow-ups', cadence: 'Seasonal',
    desc: 'Seasonal promotions — Aria writes the email for whatever fits the season (a winter special, etc.), you review it, then send it to your whole list.',
    includes: ['AI-written promo emails', 'Blast your whole list'] },
  { key: 'expenses', group: 'year', title: 'Expenses & books', cadence: 'Weekly',
    desc: 'Keeps your books — scan a statement photo or connect your bank/card — so every dollar out is tracked and your taxes are ready.',
    includes: ['Scan a statement photo', 'Or connect bank/card (Plaid)'] },
  { key: 'taxes', group: 'year', title: 'Quarterly taxes', cadence: 'Quarterly',
    desc: 'Updates your estimated quarterly taxes after every job — always set aside for the worst case, so you’re never caught short at filing time.',
    includes: ['Recalculated each job', 'Worst-case set-aside'] },
  { key: 'reviews', group: 'year', title: 'Reviews', cadence: 'Ongoing',
    desc: 'Watches your Google reviews and replies to each one — warm, a little different every time, always inviting them to tell friends & family.',
    includes: ['Google reviews', 'Auto-replies, never copy-paste'] },
  { key: 'licensing', group: 'year', title: 'Licensing & insurance', cadence: 'Yearly',
    desc: 'Tracks every license and insurance renewal date and reminds you before it lapses — with a link to take care of it.',
    includes: ['Renewal date tracker', 'Reminders + a renew link'] },
];

// Per-agent "Need some help?" setup flows — the SHORT opening question + a
// grounded checklist the owner taps. Authored 2026-06-17 from each panel.
// Selections persist to localStorage[storageKey] and are summarized to Aria.
const SETUP_FLOWS = {
  calendar: { greeting: 'Wanna connect *your* Google Calendar, or have me make a new one for you?',
    preChoice: { question: '', storageKey: 'howto_setup_calendar_choice', options: [
      { id: 'google', label: 'Connect mine', summary: "On it — I'll point you through it." },
      { id: 'work', label: 'Make me a new one', summary: "Making you a fresh work calendar now — no Google account needed. One second." } ] },
    checklist: null },
  leads: { greeting: 'What kind of leads do you already use?', checklist: { storageKey: 'howto_setup_leads', confirmLabel: 'Pull these in', summaryPrefix: 'For leads I use', options: [
    { id: 'instagram', label: 'Instagram' }, { id: 'facebook', label: 'Facebook' }, { id: 'google', label: 'Google' }, { id: 'website', label: 'Website' }, { id: 'referral', label: 'Referrals' }, { id: 'yelp', label: 'Yelp' }, { id: 'nextdoor', label: 'Nextdoor' }, { id: 'word_of_mouth', label: 'Word of mouth' } ] } },
  quotes: { greeting: 'When a job comes in, what should I use to price the estimate?', checklist: { storageKey: 'howto_setup_quotes', confirmLabel: 'Use these', summaryPrefix: 'For pricing estimates, use', options: [
    { id: 'job_photos', label: 'Photos from the job' }, { id: 'job_title_scope', label: 'The job title and scope' }, { id: 'my_price_list', label: 'My own price list / rates' }, { id: 'past_quotes', label: "What I've charged on similar jobs" }, { id: 'hourly_rate', label: 'My hourly labor rate' }, { id: 'material_costs', label: 'Materials and supply costs' }, { id: 'local_market', label: 'Going rates in my area' }, { id: 'always_review', label: "Always let me edit before it's sent" } ] } },
  invoicing: { greeting: "When a job's done, want the invoice sent by text or email? Here's the blueprint I'd use — like it?",
    preChoice: { question: 'Send invoices by…', storageKey: 'howto_setup_invoicing_delivery', options: [
      { id: 'text', label: 'Text', summary: 'Send invoices by text' },
      { id: 'email', label: 'Email', summary: 'Send invoices by email' },
      { id: 'both', label: 'Both', summary: 'Send invoices by text and email' } ] },
    checklist: { storageKey: 'howto_setup_invoicing', confirmLabel: 'Looks good — set it up', summaryPrefix: 'And', options: [
      { id: 'review_first', label: 'Let me review each one before it sends' },
      { id: 'auto_send', label: "Auto-send the moment a job's marked done" } ] } },
  payments: { greeting: "Let's set up how you get paid.",
    preChoice: { question: 'Do you allow financing?', storageKey: 'howto_setup_payments_financing', options: [
      { id: 'yes', label: 'Yes', summary: 'I offer financing (the partner runs the quick check at checkout — I never see their credit)' },
      { id: 'no', label: 'No — pay in full', summary: "I don't offer financing — pay in full only" } ] },
    checklist: { storageKey: 'howto_setup_payments', confirmLabel: "That's how I take payment", summaryPrefix: 'Customers can pay by', question: 'Which ways can customers pay?', options: [
      { id: 'card', label: 'Card (Visa / MC / Amex)' }, { id: 'bank_ach', label: 'Bank transfer (ACH)' }, { id: 'check', label: 'Check' }, { id: 'apple_pay', label: 'Apple Pay' }, { id: 'google_pay', label: 'Google Pay' } ] } },
  followups: { greeting: 'Which seasonal promos do you want Aria to write for you?', checklist: { storageKey: 'howto_setup_followups', confirmLabel: 'Write these for me', summaryPrefix: 'Write these seasonal promos', options: [
    { id: 'winter_special', label: 'Winter special' }, { id: 'spring_cleanup', label: 'Spring cleanup' }, { id: 'summer_special', label: 'Summer special' }, { id: 'fall_maintenance', label: 'Fall maintenance' }, { id: 'holiday_thank_you', label: 'Holiday thank-you' } ] } },
  expenses: { greeting: 'What do you use to pay for business stuff?', checklist: { storageKey: 'howto_setup_expenses', confirmLabel: 'Track these', summaryPrefix: 'I pay for business stuff with', options: [
    { id: 'business_bank', label: 'Business bank account' }, { id: 'personal_bank', label: 'Personal bank account' }, { id: 'business_card', label: 'Business credit/debit card' }, { id: 'personal_card', label: 'Personal card for work stuff' }, { id: 'cash', label: 'Cash' }, { id: 'paper_receipts', label: "Paper receipts I'll snap photos of" }, { id: 'paypal_venmo', label: 'PayPal / Venmo / Cash App' }, { id: 'gas_card', label: 'Gas / fuel card' } ] } },
  taxes: { greeting: 'What should I factor into your quarterly set-aside?', checklist: { storageKey: 'howto_setup_taxes', confirmLabel: 'Set these up', summaryPrefix: 'For my quarterly set-aside, factor in', options: [
    { id: 'job_income', label: 'My completed jobs (net & gross)' }, { id: 'cash_jobs', label: 'Cash jobs not in the system' }, { id: 'other_income', label: 'Other income (side work, sales)' }, { id: 'worst_case_30', label: 'Use the 30% worst-case buffer' }, { id: 'split_quarterly', label: 'Split it into 4 quarterly payments' }, { id: 'remind_deadlines', label: 'Remind me before each quarterly deadline' }, { id: 'state_taxes', label: 'Include my state taxes too' }, { id: 'have_accountant', label: 'I have an accountant to confirm with' } ] } },
  reviews: { greeting: 'Where do customers leave you reviews?', checklist: { storageKey: 'howto_setup_reviews', confirmLabel: 'Connect reviews', summaryPrefix: 'Customers review me on', options: [
    { id: 'google', label: 'Google' }, { id: 'yelp', label: 'Yelp' }, { id: 'facebook', label: 'Facebook' }, { id: 'nextdoor', label: 'Nextdoor' }, { id: 'angi', label: 'Angi / HomeAdvisor' }, { id: 'bbb', label: 'Better Business Bureau' }, { id: 'thumbtack', label: 'Thumbtack' }, { id: 'website', label: 'My own website' }, { id: 'not_sure', label: 'Not sure yet' } ] } },
  licensing: { greeting: 'Which licenses and insurance do you need to keep current?', checklist: { storageKey: 'howto_setup_licensing', confirmLabel: 'Track these', summaryPrefix: 'Keep these current for me', options: [
    { id: 'business_license', label: 'Business license' }, { id: 'trade_license', label: 'Trade / contractor license' }, { id: 'general_liability', label: 'General liability insurance' }, { id: 'workers_comp', label: "Workers' comp insurance" }, { id: 'commercial_auto', label: 'Commercial auto insurance' }, { id: 'surety_bond', label: 'Surety bond' }, { id: 'vehicle_registration', label: 'Vehicle registration / DOT' }, { id: 'professional_cert', label: 'Professional certification' }, { id: 'other', label: 'Something else' } ] } },
};

// The agent's individual services (mirror of HowTo-Backend/services/operator).
// `ready` reflects provider config — flip as keys land (Resend in; Stripe/Twilio/
// Google pending). `suits` drives which jobs Aria suggests per business type.
// ── THE FULL OWNER CHECKLIST (Braeden 8/13) ────────────────────────────────
// "act like your an owner and there should be everything you want to check
//  like on a daily bases or once a year that can be computer automated —
//  make sure its all there when you make a business profile for someone"
//
// This catalog used to hold FOUR entries. A real owner's year is not four
// things, so a business profile built from it was missing most of the job.
// The list below is organised the way an owner actually thinks — by HOW OFTEN
// it comes around — and covers the recurring work a computer can genuinely do.
//
// HONESTY RULE: `ready` means it runs TODAY. Everything else says exactly what
// it is waiting on. Four of these have a live service behind them
// (HowTo-Backend/services/operator/services/*); the rest are the map, listed so
// an owner sees the whole job and what is switched on so far. Nothing here
// claims to work that doesn't — a list that oversells is worse than a short one.
//
// `group` = the cadence heading it sits under. `suits` = which business types
// Aria suggests it for. Keys of the four live ones are UNCHANGED so saved
// enabledServices configs keep working.
const AGENT_CAPS = [
  // ── EVERY DAY ────────────────────────────────────────────────────────────
  { key: 'missed_calls',     label: 'Missed calls',        detail: 'Texts back in seconds so the job is not lost', group: 'Every day',    ready: false, needs: 'Needs Twilio key',      suits: ['trades', 'services', 'health', 'retail', 'freelance'] },
  { key: 'calendar_invites', label: 'Calendar invites',    detail: 'Book jobs & send reminders',                   group: 'Every day',    ready: false, needs: 'Needs Google Calendar', suits: ['services', 'trades', 'health'] },
  { key: 'text_reminders',   label: 'Text reminders',      detail: 'Send & receive customer SMS',                  group: 'Every day',    ready: false, needs: 'Needs Twilio key',      suits: ['trades', 'services', 'health', 'retail'] },
  { key: 'new_leads',        label: 'New leads',           detail: 'Every lead into one list, whatever the source', group: 'Every day',   ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'retail', 'marketing', 'freelance', 'health'] },
  { key: 'days_route',       label: "Today's route",       detail: 'Puts the day in drive order, least backtrack',  group: 'Every day',   ready: false, needs: 'Needs Google Maps',     suits: ['trades', 'services'] },
  { key: 'job_photos',       label: 'Job photos',          detail: 'Before & after saved to the job — your proof',  group: 'Every day',   ready: false, needs: 'Needs MMS media ingest', suits: ['trades', 'services'] },

  // ── EVERY JOB ────────────────────────────────────────────────────────────
  { key: 'quotes',           label: 'Quotes & estimates',  detail: 'Priced from the scope, you edit before it goes', group: 'Every job',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'freelance', 'marketing'] },
  { key: 'invoices',         label: 'Invoices',            detail: 'Create, send & chase invoices',                 group: 'Every job',   ready: false, needs: 'Needs Stripe key',      suits: ['trades', 'services', 'retail', 'freelance'] },
  { key: 'payments',         label: 'Payments',            detail: 'Pay link — card, bank, or financing',           group: 'Every job',   ready: false, needs: 'Needs Stripe key',      suits: ['trades', 'services', 'retail', 'freelance'] },
  { key: 'crew_hours',       label: 'Crew hours',          detail: 'Clock in/out per job — feeds payroll & costing', group: 'Every job',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services'] },

  // ── EVERY WEEK ───────────────────────────────────────────────────────────
  { key: 'quote_chase',      label: 'Quotes with no reply', detail: 'Nudges the ones that went quiet',              group: 'Every week',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'freelance', 'marketing'] },
  { key: 'overdue_invoices', label: 'Overdue invoices',    detail: 'Reminders that escalate until you are paid',    group: 'Every week',  ready: false, needs: 'Needs Stripe key',      suits: ['trades', 'services', 'retail', 'freelance'] },
  { key: 'expenses',         label: 'Receipts & spend',    detail: 'Logs every dollar out, categorised',            group: 'Every week',  ready: false, needs: 'Needs bank/card link',  suits: ['trades', 'services', 'retail', 'freelance', 'marketing', 'health'] },
  { key: 'supplies',         label: 'Supplies & materials', detail: 'Reorder before you run out — fuel, dump, bags', group: 'Every week', ready: false, needs: 'Being wired up',        suits: ['trades', 'retail'] },
  { key: 'lead_sources',     label: 'Where jobs came from', detail: 'What each source cost vs the work it won',     group: 'Every week',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'retail', 'marketing', 'freelance'] },

  // ── EVERY MONTH ──────────────────────────────────────────────────────────
  { key: 'reviews',          label: 'Reviews',             detail: 'Asks happy customers, replies to every one',    group: 'Every month', ready: false, needs: 'Needs Google Business', suits: ['trades', 'services', 'retail', 'health', 'freelance'] },
  { key: 'email_promotions', label: 'Email promotions',    detail: 'Send promos & replies',                         group: 'Every month', ready: true,  needs: 'Connected',             suits: ['retail', 'marketing', 'services'] },
  { key: 'payroll',          label: 'Payroll & contractors', detail: 'Hours to pay, and who is a 1099',             group: 'Every month', ready: false, needs: 'Needs payroll provider', suits: ['trades', 'services', 'retail', 'health'] },
  { key: 'repeat_customers', label: 'Repeat customers',    detail: 'Service agreements billed on schedule',         group: 'Every month', ready: false, needs: 'Needs Stripe key',      suits: ['services', 'trades', 'health'] },
  { key: 'vehicle_upkeep',   label: 'Truck & equipment',   detail: 'Service due by mileage or hours, before it breaks', group: 'Every month', ready: false, needs: 'Being wired up',    suits: ['trades'] },
  { key: 'gbp_posts',        label: 'Google Business page', detail: 'Keeps the map listing active with real posts',  group: 'Every month', ready: false, needs: 'Needs Google Business', suits: ['trades', 'services', 'retail', 'health'] },

  // ── EVERY QUARTER ────────────────────────────────────────────────────────
  { key: 'taxes',            label: 'Quarterly taxes',     detail: 'Sets aside as you earn, files on time',         group: 'Every quarter', ready: false, needs: 'Being wired up',      suits: ['trades', 'services', 'retail', 'freelance', 'marketing', 'health'] },
  { key: 'price_check',      label: 'Price & margin check', detail: 'Flags jobs where your cost outran the price',   group: 'Every quarter', ready: false, needs: 'Being wired up',      suits: ['trades', 'services', 'retail', 'freelance'] },
  { key: 'vendor_papers',    label: 'Sub & vendor paperwork', detail: 'W-9s and insurance certs before they work',   group: 'Every quarter', ready: false, needs: 'Being wired up',      suits: ['trades', 'services'] },

  // ── EVERY YEAR ───────────────────────────────────────────────────────────
  { key: 'renewals',         label: 'Renewals & filings',  detail: 'License, insurance, vehicle reg, state report',  group: 'Every year',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'retail', 'health', 'freelance', 'marketing'] },
  { key: 'year_end',         label: 'Year-end books',      detail: '1099s, W-2s, and the package your CPA wants',    group: 'Every year',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'retail', 'freelance', 'marketing', 'health'] },
  { key: 'winback',          label: 'Win back old customers', detail: 'Reaches out to anyone quiet 12 months',       group: 'Every year',  ready: false, needs: 'Being wired up',        suits: ['trades', 'services', 'retail', 'health'] },
];

// Cadence headings, in the order an owner lives them.
const CAP_GROUP_ORDER = ['Every day', 'Every job', 'Every week', 'Every month', 'Every quarter', 'Every year'];

const BIZ_CATEGORIES = [
  { cat: 'trades',    re: /hvac|plumb|electric|roof|construct|landscap|clean|junk|haul|contractor|repair|paint/ },
  { cat: 'retail',    re: /shop|store|retail|ecommerce|boutique|sell|merch/ },
  { cat: 'marketing', re: /market|agency|ads|social|brand|design|seo/ },
  { cat: 'health',    re: /clinic|dental|medic|health|therap|salon|spa|wellness|fitness/ },
  { cat: 'freelance', re: /freelanc|consult|coach|solo|creator|tutor/ },
];
function categorizeBiz(text) {
  const t = String(text || '').toLowerCase();
  const hit = BIZ_CATEGORIES.find((c) => c.re.test(t));
  return hit ? hit.cat : 'services';
}
function suggestedKeys(text) {
  const cat = categorizeBiz(text);
  const s = AGENT_CAPS.filter((c) => (c.suits || []).includes(cat)).map((c) => c.key);
  return s.length ? s : AGENT_CAPS.map((c) => c.key);
}

const PERIOD_LABEL = { day: 'today', week: 'this week', month: 'this month', year: 'this year' };

// Drill-down titles + formatters for the "click a number → see the list" panel.
const METRIC_TITLE = { revenue: 'Revenue', gross: 'Jobs completed', expenses: 'Expenses', unpaid: 'Unpaid / overdue', net: 'Net — the owner’s profit' };
const fmtMoney = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtDate = (at) => {
  if (!at) return '';
  try { return new Date(String(at).replace(' ', 'T') + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch (_) { return String(at); }
};

// ── Styles lifted from aria-ceo-apple.html, scoped under .ceo ──────────────
const STYLE = `
.ceo {
  --c-ok:#30d158; --c-warn:#ffd60a; --c-info:#5e5ce6; --c-cyan:#0a84ff; --c-alert:#ff453a;
  --c-ok-d:#1F7A38; --c-info-d:#0040DD; --c-alert-d:#C8161A;
  --text:rgba(255,255,255,0.96); --text-2:rgba(255,255,255,0.55); --text-3:rgba(255,255,255,0.30);
  --bg-deep:#08080d;
  position:relative; min-height:100%; padding:0 0 70px;
  background:transparent;
  color:var(--text);
  font-family:-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif;
  font-feature-settings:'tnum'; -webkit-font-smoothing:antialiased;
}
/* NIGHT mode: no separate panel either — let the page background show through
   edge-to-edge so the Company page is one flat color (no "big square"), matching
   the day-mode fix. Both modes stay consistent. */
.ceo::before {
  content:''; position:absolute; inset:0; z-index:0; pointer-events:none;
  background: transparent;
}
.ceo > * { position:relative; z-index:1; }
/* Hero bottom padding leaves room for the round logo badge that hangs ~70px
   below the banner's bottom edge (classic profile-badge overlap). */
.ceo-hero.ceo-hero--brand { display:block; padding:0 0 26px; }
.ceo .ceo-banner { position:absolute; top:-28px; left:50%; transform:translateX(-50%); width:calc(100vw + 60px); height:calc(clamp(300px, 38vh, 560px) + 28px); display:flex; overflow:hidden; z-index:-3; }
/* Decorations (banner, logo badge, photo wall) live BEHIND all content. .wrap is
   a stacking context (isolate) and the decorations sit at negative z, so buttons
   and info are ALWAYS painted on top and never covered — at any screen size. */
.ceo .wrap { isolation: isolate; }
.ceo .ceo-banner img { flex:1; min-width:0; object-fit:cover; }
.ceo .ceo-banner-spacer { height:clamp(300px, 38vh, 560px); }
.ceo .ceo-brandcard { position:absolute; top:calc(clamp(300px, 38vh, 560px) * 0.45); left:50%; transform:translate(-50%,-50%); z-index:2; display:flex; flex-direction:column; align-items:center; background:none; border:none; box-shadow:none; padding:0; }

/* The round logo badge — half-overlapping the banner's bottom edge, centered. */
.ceo .ceo-avatar { display:block; position:absolute; top:clamp(280px, 34vh, 520px); left:50%; margin-left:calc(-50vw + 2.5vw); transform:translateY(-50%); z-index:-1; width:clamp(220px, 34vh, 460px); height:clamp(220px, 34vh, 460px); border-radius:50%; object-fit:cover; border:0.4vh solid rgba(255,255,255,.95); box-shadow:0 8px 26px rgba(0,0,0,.4); background:#0e130a; }
/* Brand-green gradient title + subtle glow — placeholder treatment until the
   layout generator (ARIA_BRAND_LAYOUT_GENERATOR.md) replaces it per brand. */
.ceo .ceo-bizname { white-space:nowrap; text-align:center; font-size:clamp(36px, 5vw, 120px); font-weight:800; letter-spacing:.5px; margin:0; background:linear-gradient(180deg,var(--titleG1,#b8e04e),var(--titleG2,#91ba38) 55%,var(--titleG3,#6e9527)); -webkit-background-clip:text; background-clip:text; color:transparent; -webkit-text-stroke:var(--titleStrokeW,2px) var(--titleStroke,rgba(16,21,12,.9)); filter:drop-shadow(0 0 16px rgba(0,0,0,.55)) drop-shadow(0 0 26px rgba(255,255,255,.28)); }
.ceo.theme-light .ceo-bizname { -webkit-text-stroke:var(--titleStrokeWL,1.6px) var(--titleStroke,rgba(16,21,12,.95)); filter:drop-shadow(0 0 10px rgba(255,255,255,.85)) drop-shadow(0 0 24px rgba(255,255,255,.5)); }
/* No-photos fallback: honest brand-gradient banner + logo watermark (never
   stock or AI "job" photos — the page must not lie about work not done). */
.ceo .ceo-banner--gradient { background:var(--bannerGrad, linear-gradient(135deg,#101014,#26262e)); align-items:center; justify-content:center; }
.ceo .ceo-banner--gradient .ceo-banner__wm { flex:none; width:clamp(160px,30vh,380px); height:auto; opacity:.16; border-radius:50%; filter:saturate(0.6); }
.ceo .ceo-banner img { flex:1; min-width:0; object-fit:cover; }

.ceo .wrap { max-width:1000px; margin:0 auto; padding:0 32px; }

/* Top bar: orb + toggles */
.ceo-head { display:flex; align-items:center; gap:14px; padding:6px 0 14px; }
.ceo .orb {
  width:34px; height:34px; border-radius:50%; flex-shrink:0;
  background:
    radial-gradient(circle at 30% 25%, rgba(255,255,255,0.4), transparent 50%),
    radial-gradient(circle at 35% 35%, var(--accent-hover) 0%, #30d158 35%, #14532d 100%);
  box-shadow:0 0 22px rgba(48,209,88,0.5);
  transition:transform 0.4s, box-shadow 0.4s;
}
.ceo-head:hover .orb { transform:scale(1.08); box-shadow:0 0 32px rgba(48,209,88,0.7); }
.ceo-toggles { margin-left:auto; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ceo .range, .ceo .theme-toggle {
  display:inline-flex; padding:3px;
  background:rgba(255,255,255,0.05); border:0.5px solid rgba(255,255,255,0.08); border-radius:999px;
}
.ceo .range > button {
  padding:7px 16px; font-size:12px; font-weight:600; letter-spacing:0.04em;
  color:var(--text-2); cursor:pointer; border-radius:999px; border:none; background:transparent;
  font-family:inherit; transition:color 0.18s, background 0.18s;
}
.ceo .range > button:hover { color:var(--text); }
.ceo .range > button.on { background:rgba(255,255,255,0.12); color:var(--text); box-shadow:inset 0 0.5px 0 rgba(255,255,255,0.18); }
.ceo .theme-opt {
  display:inline-flex; align-items:center; gap:6px; padding:6px 13px;
  background:transparent; border:none; border-radius:999px;
  font-size:12px; font-weight:600; color:var(--text-2); cursor:pointer;
  letter-spacing:0.04em; font-family:inherit; transition:color 0.18s, background 0.18s;
}
.ceo .theme-opt svg { width:13px; height:13px; }
.ceo .theme-opt:hover { color:var(--text); }
.ceo .theme-opt.on { background:rgba(255,255,255,0.12); color:var(--text); box-shadow:inset 0 0.5px 0 rgba(255,255,255,0.18); }

/* Gradient hero — the business name */
.ceo-hero { display:flex; justify-content:center; padding:8px 0 30px; }
.ceo .pt-h {
  font-size:clamp(34px, 6vw, 68px); font-weight:800; letter-spacing:-0.04em; line-height:1; text-align:center;
  background:linear-gradient(180deg, var(--text) 0%, var(--text-2) 100%);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
}

.ceo-kicker { margin:-18px 0 12px; }

/* Glass bubble KPI cards */
.ceo-grid { display:grid; gap:16px; margin-bottom:16px; }
.ceo .bub {
  position:relative; overflow:hidden; border-radius:22px; padding:22px 24px;
  background:#141a24; border:1px solid rgba(255,255,255,0.14);
  backdrop-filter:blur(40px) saturate(180%); -webkit-backdrop-filter:blur(40px) saturate(180%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 24px rgba(0,0,0,0.4);
  transition:transform 0.3s cubic-bezier(0.2,0.8,0.2,1), border-color 0.3s, box-shadow 0.3s;
}
.ceo .bub::before {
  content:''; position:absolute; inset:0; pointer-events:none; border-radius:inherit;
  background:radial-gradient(ellipse 60% 40% at 20% 0%, rgba(255,255,255,0.10), transparent 70%);
}
.ceo .bub { cursor:pointer; }
.ceo .bub--neutral { background:#1d4ed8; border-color:rgba(59,130,246,0.45); box-shadow:inset 0 1px 0 rgba(147,197,253,0.25), 0 8px 24px rgba(0,0,0,0.4); }
.ceo.theme-light .bub--neutral { background:#1a73e8; border-color:#1a73e8; }
.ceo .bub:hover { transform:translateY(-5px) scale(1.025); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 20px 40px rgba(0,0,0,0.5); }
.ceo .bub.good  { background:linear-gradient(155deg, rgba(48,209,88,0.12), rgba(48,209,88,0.05) 70%), #101a12; border-color:rgba(48,209,88,0.28); }
.ceo .bub.good:hover  { border-color:rgba(48,209,88,0.5); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 0 28px rgba(48,209,88,0.20), 0 12px 28px rgba(0,0,0,0.5); }
.ceo .bub.bad   { background:linear-gradient(155deg, rgba(255,69,58,0.12), rgba(255,69,58,0.05) 70%), #1a1010; border-color:rgba(255,69,58,0.30); }
.ceo .bub.bad:hover   { border-color:rgba(255,69,58,0.55); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 0 28px rgba(255,69,58,0.20), 0 12px 28px rgba(0,0,0,0.5); }
.ceo .bub.neutral:hover { border-color:rgba(255,255,255,0.22); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 12px 28px rgba(0,0,0,0.5); }
.ceo .bub-label { position:relative; z-index:2; font-size:12px; font-weight:600; letter-spacing:0.02em; line-height:1.2; color:var(--text-2); }
.ceo .bub.good .bub-label { color:var(--c-ok); }
.ceo .bub.bad  .bub-label { color:var(--c-alert); }
.ceo .bub-value { position:relative; z-index:2; margin-top:10px; font-size:32px; font-weight:700; letter-spacing:-0.025em; line-height:1; color:var(--text); font-variant-numeric:tabular-nums; }
.ceo .bub.good .bub-value { color:var(--c-ok); text-shadow:0 0 18px rgba(48,209,88,0.30); }
.ceo .bub.bad  .bub-value { color:var(--c-alert); text-shadow:0 0 18px rgba(255,69,58,0.30); }
.ceo .bub.warn { background:linear-gradient(155deg, rgba(255,214,10,0.32), rgba(255,214,10,0.14) 70%), #201a06; border-color:rgba(255,214,10,0.30); }
.ceo .bub.warn:hover { border-color:rgba(255,214,10,0.55); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 0 28px rgba(255,214,10,0.20), 0 12px 28px rgba(0,0,0,0.5); }
.ceo .bub.warn .bub-label { color:var(--c-warn); }
.ceo .bub.warn .bub-value { color:var(--c-warn); text-shadow:0 0 18px rgba(255,214,10,0.30); }

/* Aria's Work — the cyan "Today's tasks" treatment */
.ceo-today { position:relative; margin-top:36px; padding:32px 0 0; }
.ceo-today::before {
  content:''; position:absolute; inset:0; z-index:-1; pointer-events:none;
  background:none;
}
.ceo .today-big-title {
  font-size:clamp(28px, 4.4vw, 46px); font-weight:800; letter-spacing:-0.025em; line-height:1; margin:0 0 6px; text-align:center;
  background:linear-gradient(180deg, #ffffff 0%, #0a84ff 100%);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  filter:drop-shadow(0 4px 20px rgba(10,132,255,0.25));
}
.ceo .today-kicker {
  font-size:12px; letter-spacing:0.26em; text-transform:uppercase; color:#0a84ff; font-weight:700;
  text-shadow:0 0 16px rgba(10,132,255,0.45); margin:24px 0 12px; text-align:center;
}
.ceo .today-kicker .live-dot {
  display:inline-block; width:7px; height:7px; border-radius:50%; background:#0a84ff;
  margin-right:46px; vertical-align:1px; box-shadow:0 0 10px #0a84ff;
  animation:ceoDot 2.4s ease-in-out infinite;
}
@keyframes ceoDot { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
.ceo .today-sub { text-align:center; font-size:13.5px; color:var(--text-2); font-weight:500; margin:0 0 26px; }
.ceo .today-sub .num { color:#0a84ff; font-weight:700; }

.ceo .task-list { max-width:720px; margin:0 auto; display:flex; flex-direction:column; gap:12px; }
.ceo button.task {
  position:relative; overflow:hidden; width:100%; text-align:left; font-family:inherit; cursor:pointer;
  display:grid; grid-template-columns:32px 1fr auto auto; gap:16px; align-items:center;
  padding:14px 20px; border-radius:16px;
  background:linear-gradient(155deg, rgba(10,132,255,0.06), rgba(10,132,255,0.02) 70%), rgba(8,12,20,0.5);
  backdrop-filter:blur(40px) saturate(180%); -webkit-backdrop-filter:blur(40px) saturate(180%);
  border:0.5px solid rgba(10,132,255,0.18);
  box-shadow:inset 0 1px 0 rgba(255,255,255,0.12), 0 12px 32px rgba(0,0,0,0.4);
  transition:transform 0.3s cubic-bezier(0.2,0.8,0.2,1), border-color 0.3s, box-shadow 0.3s;
}
.ceo button.task::before {
  content:''; position:absolute; inset:0; pointer-events:none; border-radius:inherit;
  background:radial-gradient(ellipse 50% 30% at 20% 0%, rgba(255,255,255,0.10), transparent 70%);
}
.ceo button.task:hover {
  transform:translateY(-3px);
  background:linear-gradient(155deg, rgba(10,132,255,0.06), rgba(10,132,255,0.015) 70%), rgba(2,4,8,0.85);
  border-color:rgba(10,132,255,0.5);
  box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 0 0 1px rgba(10,132,255,0.22), 0 0 36px rgba(10,132,255,0.25), 0 16px 36px rgba(0,0,0,0.5);
}
/* every task button (incl. not-yet-wired ones) gets the hover shade */
.ceo .t-num {
  width:32px; height:32px; border-radius:50%; position:relative; z-index:2; flex-shrink:0;
  background:rgba(10,132,255,0.12); border:0.5px solid rgba(10,132,255,0.4); color:#0a84ff;
  display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700;
  box-shadow:0 0 14px rgba(10,132,255,0.25), inset 0 1px 0 rgba(255,255,255,0.15);
}
.ceo .t-body { position:relative; z-index:2; display:flex; align-items:baseline; gap:12px; min-width:0; flex:1; overflow:hidden; }
.ceo .t-title { font-size:14.5px; font-weight:600; color:var(--text); letter-spacing:-0.005em; white-space:nowrap; flex-shrink:0; }
.ceo .t-sub { font-size:12px; color:var(--text-2); font-weight:400; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; flex:1; }
.ceo .t-aria {
  position:relative; z-index:2; white-space:nowrap;
  font-size:9.5px; font-weight:700; color:#0a84ff; letter-spacing:0.06em; text-transform:uppercase;
  padding:4px 9px; background:rgba(10,132,255,0.12); border:0.5px solid rgba(10,132,255,0.35); border-radius:999px;
}
.ceo .t-time { position:relative; z-index:2; font-size:15px; font-weight:600; color:var(--text-3); min-width:18px; text-align:right; transition:color 0.2s, transform 0.2s; }
.ceo button.task:hover .t-time { color:#0a84ff; transform:translateX(2px); }

/* ── Day (light) mode — Apple light variant ── */
.ceo.theme-light {
  --text:#1d1d1f; --text-2:rgba(60,60,67,0.78); --text-3:rgba(60,60,67,0.50);
  --bg-deep:#F2F2F7; --c-ok:#1F7A38; --c-alert:#C8161A; --c-warn:#8a6300;
}
/* Day mode: NO separate panel — let the page background show through edge-to-edge
   so the Company page is one flat color (no "big square" / border). */
.ceo.theme-light::before {
  background: transparent;
}
.ceo.theme-light .range, .ceo.theme-light .theme-toggle { background:rgba(0,0,0,0.05); border-color:rgba(0,0,0,0.08); }
.ceo.theme-light .range > button.on, .ceo.theme-light .theme-opt.on { background:rgba(0,0,0,0.08); color:#1d1d1f; box-shadow:inset 0 0.5px 0 rgba(255,255,255,0.6); }
.ceo.theme-light .pt-h { background:linear-gradient(180deg, #1d1d1f 0%, rgba(60,60,67,0.7) 100%); -webkit-background-clip:text; background-clip:text; }
/* Day mode boxes: full vibrant color fill + dark outline */
.ceo.theme-light .bub { padding:18px 20px; background:#fff; border:1.5px solid rgba(0,0,0,0.5); box-shadow:0 6px 16px rgba(0,0,0,0.16); }
.ceo.theme-light .bub::before { display:none; }
.ceo.theme-light .bub-label { margin:0; padding:0; background:transparent; border:none; font-size:12px; font-weight:600; letter-spacing:0.01em; }
.ceo.theme-light .bub-value { margin-top:8px; padding:0; }
.ceo.theme-light .bub.good    { background:linear-gradient(160deg, #1a9d47 0%, #0e6e30 100%); border-color:rgba(0,0,0,0.5); }
.ceo.theme-light .bub.warn    { background:linear-gradient(160deg, #ffd21f 0%, #e0a200 100%); border-color:rgba(0,0,0,0.5); }
.ceo.theme-light .bub.bad     { background:linear-gradient(160deg, #e23b39 0%, #ab1014 100%); border-color:rgba(0,0,0,0.5); }
.ceo.theme-light .bub.neutral { background:linear-gradient(160deg, #2a86f0 0%, #0a52c0 100%); border-color:rgba(0,0,0,0.5); }
.ceo.theme-light .bub.good .bub-label, .ceo.theme-light .bub.bad .bub-label, .ceo.theme-light .bub.neutral .bub-label { color:rgba(255,255,255,0.9); }
.ceo.theme-light .bub.good .bub-value, .ceo.theme-light .bub.bad .bub-value, .ceo.theme-light .bub.neutral .bub-value { color:#fff; text-shadow:0 1px 3px rgba(0,0,0,0.22); }
.ceo.theme-light .bub.warn .bub-label { color:rgba(60,42,0,0.82); }
.ceo.theme-light .bub.warn .bub-value { color:#3a2a00; text-shadow:none; }
.ceo.theme-light button.task { background:linear-gradient(160deg, #3a9bff 0%, #0a63ff 100%); border:1px solid rgba(0,0,0,0.45); box-shadow:0 6px 16px rgba(0,0,0,0.16); }
.ceo.theme-light .t-title, .ceo.theme-light .t-title b { color:#fff; }
.ceo.theme-light .t-sub { color:rgba(255,255,255,0.82); }
.ceo.theme-light .t-sub .sep { color:rgba(255,255,255,0.5); }
.ceo.theme-light .t-time { color:rgba(255,255,255,0.9); }
.ceo.theme-light button.task:hover { background:linear-gradient(160deg, #4aa6ff 0%, #1a72ff 100%); border-color:rgba(0,0,0,0.55); box-shadow:0 16px 34px rgba(0,60,160,0.40); }
.ceo.theme-light .today-big-title { background:linear-gradient(180deg, #0a52c0 0%, #007aff 100%); -webkit-background-clip:text; background-clip:text; filter:none; }
.ceo.theme-light .today-kicker { color:#0a5fd0; text-shadow:none; }
.ceo.theme-light .t-num { background:rgba(255,255,255,0.22); border-color:rgba(255,255,255,0.6); color:#fff; box-shadow:inset 0 1px 0 rgba(255,255,255,0.3); }
.ceo.theme-light .t-aria { color:#fff; background:rgba(255,255,255,0.22); border-color:rgba(255,255,255,0.5); }
.ceo.theme-light .bub:hover { box-shadow:0 20px 40px rgba(0,0,0,0.32); }
.ceo.theme-light .ceo-today::before {
  background:none;
}

@media (max-width:560px){
  .ceo .wrap { padding:0 18px; }
  .ceo .ceo-grid { grid-template-columns:1fr !important; }
  .ceo button.task { grid-template-columns:28px 1fr auto; }
  .ceo .t-time { display:none; }
  .ceo .t-body { flex-wrap:wrap; gap:2px 12px; }
  .ceo .t-sub { white-space:normal; }
}

/* ── 24/7 Agent hero + setup ─────────────────────────────────────────── */
.ceo .agent-hero {
  max-width:720px; margin:0 auto 18px; display:flex; align-items:center; gap:16px;
  padding:16px 18px; border-radius:18px;
  background:linear-gradient(160deg, #0a84ff 0%, #0a5fd0 100%);
  border:1px solid rgba(255,255,255,0.18);
  box-shadow:0 10px 30px rgba(0,60,160,0.35);
  transition:opacity .2s, filter .2s, box-shadow .2s;
}
.ceo .agent-hero--off {
  background:linear-gradient(160deg, #20202a 0%, #15151c 100%);
  border-color:rgba(255,255,255,0.08); box-shadow:0 6px 18px rgba(0,0,0,0.30);
}
.ceo .agent-hero__icon { font-size:24px; width:46px; height:46px; flex:none; display:flex; align-items:center; justify-content:center; border-radius:13px; background:rgba(255,255,255,0.16); color:#fff; }
.ceo .agent-hero--off .agent-hero__icon { color:var(--text-3); background:rgba(255,255,255,0.05); }
.ceo .agent-hero__main { flex:1; min-width:0; }
.ceo .agent-hero__title { display:flex; align-items:center; gap:8px; font-size:16px; font-weight:700; color:#fff; }
.ceo .agent-hero--off .agent-hero__title { color:var(--text-2); }
.ceo .agent-hero__sub { margin-top:3px; font-size:12.5px; line-height:1.45; color:rgba(255,255,255,0.82); }
.ceo .agent-hero--off .agent-hero__sub { color:var(--text-3); }
.ceo .agent-chips { margin-top:9px; display:flex; flex-wrap:wrap; gap:6px; }
.ceo .agent-chip { font-size:11px; font-weight:600; padding:3px 9px; border-radius:999px; background:rgba(255,255,255,0.16); color:rgba(255,255,255,0.72); }
.ceo .agent-chip.on { background:rgba(255,255,255,0.95); color:#0a63ff; }
.ceo .agent-hero__action { flex:none; }
.ceo .agent-btn { font:inherit; font-size:13px; font-weight:700; padding:10px 16px; border-radius:11px; border:none; cursor:pointer; background:#fff; color:#0a63ff; box-shadow:0 3px 10px rgba(0,0,0,0.18); transition:transform .12s, box-shadow .12s; }
.ceo .agent-btn:hover { transform:translateY(-1px); box-shadow:0 8px 20px rgba(0,0,0,0.28); }
.ceo .agent-btn--ghost { background:rgba(255,255,255,0.18); color:#fff; box-shadow:none; }

.ceo .agent-tasks { transition:opacity .2s, filter .2s; }
.ceo .agent-tasks--locked { opacity:0.45; filter:grayscale(0.8); pointer-events:none; }

.ceo .agent-setup { position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(8,8,12,0.30); backdrop-filter:blur(10px) saturate(115%); -webkit-backdrop-filter:blur(10px) saturate(115%); -webkit-app-region:no-drag; app-region:no-drag; }
.ceo .agent-setup__panel { position:relative; width:100%; max-width:460px; max-height:88vh; overflow-y:auto; padding:26px 24px 22px; border-radius:22px; background:#17171c; border:1px solid rgba(255,255,255,0.10); box-shadow:0 30px 80px rgba(0,0,0,0.55); color:var(--text); }
.ceo .agent-setup__close { position:absolute; top:14px; right:52px; width:30px; height:30px; border:none; border-radius:9px; background:rgba(255,255,255,0.08); color:var(--text-2); font-size:18px; line-height:1; cursor:pointer; }
.ceo .agent-setup__close:hover { background:rgba(255,255,255,0.16); color:#fff; }
.ceo .agent-setup__title { font-size:20px; font-weight:800; }
.ceo .agent-setup__lead { margin-top:8px; font-size:13px; line-height:1.5; color:var(--text-2); }
.ceo .agent-setup__section { margin:20px 0 10px; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-3); }
.ceo .agent-setup__caps { display:flex; flex-direction:column; gap:8px; }
.ceo .agent-cap { display:grid; grid-template-columns:14px 78px 1fr auto; align-items:center; gap:10px; padding:10px 12px; border-radius:12px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.06); }
.ceo .agent-cap__dot { width:9px; height:9px; border-radius:50%; background:var(--c-warn); }
.ceo .agent-cap.ready .agent-cap__dot { background:var(--c-ok); }
.ceo .agent-cap__name { font-size:13px; font-weight:700; }
.ceo .agent-cap__detail { font-size:12px; color:var(--text-2); }
.ceo .agent-cap__status { font-size:11px; font-weight:600; color:var(--c-warn); white-space:nowrap; }
.ceo .agent-cap.ready .agent-cap__status { color:var(--c-ok); }
.ceo .agent-setup__runloc { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.ceo .runloc { text-align:left; padding:12px 13px; border-radius:13px; cursor:pointer; background:rgba(255,255,255,0.04); border:1.5px solid rgba(255,255,255,0.10); color:var(--text); transition:border-color .15s, background .15s; }
.ceo .runloc.on { border-color:var(--c-cyan); background:rgba(10,132,255,0.14); }
.ceo .runloc__title { display:block; font-size:13px; font-weight:700; }
.ceo .runloc__sub { display:block; margin-top:3px; font-size:11px; color:var(--text-2); }
.ceo .agent-setup__activate { margin-top:18px; width:100%; padding:14px; border:none; border-radius:13px; cursor:pointer; font:inherit; font-size:15px; font-weight:800; color:#fff; background:linear-gradient(160deg, #0a84ff 0%, #0a5fd0 100%); box-shadow:0 8px 22px rgba(0,60,160,0.40); transition:transform .12s; }
.ceo .agent-setup__activate:hover { transform:translateY(-1px); }
.ceo .agent-setup__note { margin-top:11px; font-size:11.5px; line-height:1.4; color:var(--text-3); text-align:center; }

.ceo.theme-light .agent-hero--off { background:linear-gradient(160deg, #f4f5f8 0%, #e9eaef 100%); border-color:rgba(0,0,0,0.08); box-shadow:0 6px 16px rgba(0,0,0,0.10); }
.ceo.theme-light .agent-hero--off .agent-hero__icon { background:rgba(0,0,0,0.05); color:#9a9aa2; }
.ceo.theme-light .agent-hero--off .agent-hero__title { color:#3a3a40; }
.ceo.theme-light .agent-hero--off .agent-hero__sub { color:#76767e; }
.ceo.theme-light .agent-btn { background:#0a63ff; color:#fff; }
.ceo.theme-light .agent-setup__panel { background:#fff; border-color:rgba(0,0,0,0.10); color:#1d1d1f; box-shadow:0 30px 80px rgba(0,0,0,0.30); }
.ceo.theme-light .agent-setup__lead, .ceo.theme-light .agent-cap__detail, .ceo.theme-light .runloc__sub { color:#5c5c63; }
.ceo.theme-light .agent-setup__section, .ceo.theme-light .agent-setup__note { color:#8a8a92; }
.ceo.theme-light .agent-cap { background:#f5f6f9; border-color:rgba(0,0,0,0.06); }
.ceo.theme-light .agent-cap__name { color:#1d1d1f; }
.ceo.theme-light .runloc { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
.ceo.theme-light .runloc.on { border-color:#0a63ff; background:rgba(10,99,208,0.10); }
.ceo.theme-light .agent-setup__close { background:rgba(0,0,0,0.06); color:#5c5c63; }

/* Setup wizard (3 steps) */
.ceo .agent-setup__steps { display:flex; gap:6px; margin-bottom:18px; }
.ceo .agent-setup__step { width:26px; height:4px; border-radius:999px; background:rgba(255,255,255,0.14); transition:background .2s; }
.ceo .agent-setup__step.on { background:var(--c-cyan); }
.ceo .agent-setup__step.done { background:rgba(10,132,255,0.5); }
.ceo .agent-field { display:block; margin-top:14px; }
.ceo .agent-field__label { display:block; font-size:12.5px; font-weight:600; color:var(--text-2); margin-bottom:6px; }
.ceo .agent-field__input { width:100%; box-sizing:border-box; font:inherit; font-size:14px; padding:11px 13px; border-radius:11px; background:rgba(255,255,255,0.05); border:1.5px solid rgba(255,255,255,0.12); color:var(--text); }
.ceo .agent-field__input:focus { outline:none; border-color:var(--c-cyan); background:rgba(10,132,255,0.08); }
.ceo .agent-pick { width:100%; text-align:left; display:grid; grid-template-columns:22px 1fr auto; align-items:center; gap:11px; padding:11px 13px; margin-bottom:8px; border-radius:13px; cursor:pointer; background:rgba(255,255,255,0.04); border:1.5px solid rgba(255,255,255,0.10); color:var(--text); transition:border-color .14s, background .14s; }
.ceo .agent-pick.on { border-color:var(--c-cyan); background:rgba(10,132,255,0.12); }
.ceo .agent-pick__check { width:22px; height:22px; border-radius:7px; display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; color:#fff; background:rgba(255,255,255,0.10); }
.ceo .agent-pick__check.on { background:var(--c-cyan); }
.ceo .agent-pick__main { min-width:0; }
.ceo .agent-pick__name { display:flex; align-items:center; gap:8px; font-size:13.5px; font-weight:700; }
.ceo .agent-pick__rec { font-size:10px; font-weight:700; letter-spacing:0.3px; text-transform:uppercase; padding:2px 6px; border-radius:999px; background:rgba(10,132,255,0.2); color:var(--c-cyan); }
/* Cadence heading in the "which jobs should I handle?" picker. Sticky so the
   owner always knows which part of the year they are scrolling through. */
.ceo .agent-pick__group { position:sticky; top:0; z-index:2; margin:14px 0 6px; padding:6px 2px 5px; font-size:11px; font-weight:800; letter-spacing:0.12em; text-transform:uppercase; color:var(--c-cyan); background:linear-gradient(180deg, rgba(8,12,20,0.96), rgba(8,12,20,0.82)); backdrop-filter:blur(8px); border-bottom:0.5px solid rgba(255,255,255,0.10); }

/* ── Cadence blocks on the company page ("What Aria does") ───────────────
   Same three-column rhythm as .bigrow so the page reads as one list, with a
   heading per cadence and an honest on/total count. */
/* Width must track .task-list exactly (720px, or min(1100px,56vw) wide) — the
   heading is a label FOR those rows, so a full-bleed heading pushed the count
   off the right edge and broke the column the rows sit in. */
.ceo .cadence { max-width:720px; margin:26px auto 0; }
.ceo .cadence__head { display:flex; align-items:baseline; justify-content:space-between; gap:12px; margin:0 2px 10px; padding-bottom:8px; border-bottom:0.5px solid rgba(255,255,255,0.12); }
.ceo .cadence__when { font-size:12px; font-weight:800; letter-spacing:0.14em; text-transform:uppercase; color:var(--c-cyan); }
.ceo .cadence__count { font-size:11px; font-weight:700; letter-spacing:0.06em; color:rgba(255,255,255,0.42); }
.ceo .caprow { display:grid; grid-template-columns:auto minmax(150px,0.9fr) 1.5fr auto; align-items:center; gap:14px; width:100%; text-align:left; padding:15px 20px; border-radius:16px;
  background:linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)); border:0.5px solid rgba(255,255,255,0.10); }
.ceo .caprow--on { background:linear-gradient(155deg, rgba(48,209,88,0.14), rgba(48,209,88,0.03) 70%), rgba(8,12,20,0.5); border-color:rgba(48,209,88,0.42); }
.ceo .caprow__dot { width:7px; height:7px; border-radius:50%; background:rgba(255,255,255,0.22); }
.ceo .caprow--on .caprow__dot { background:#30d158; box-shadow:0 0 10px rgba(48,209,88,0.7); }
.ceo .caprow__label { font-size:15px; font-weight:700; letter-spacing:-0.2px; color:#fff; }
.ceo .caprow__detail { font-size:13px; color:rgba(255,255,255,0.55); letter-spacing:-0.1px; }
.ceo .caprow__status { justify-self:end; font-size:10.5px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; padding:4px 10px; border-radius:999px; background:rgba(255,255,255,0.07); color:rgba(255,255,255,0.45); white-space:nowrap; }
.ceo .caprow__status.ready { background:rgba(48,209,88,0.18); color:#30d158; }
@media (max-width:760px) {
  .ceo .caprow { grid-template-columns:auto 1fr; gap:6px 12px; padding:16px 18px; }
  .ceo .caprow__detail { grid-column:2; }
  .ceo .caprow__status { grid-column:2; justify-self:start; margin-top:4px; }
}
.ceo.theme-light .cadence__head { border-bottom-color:rgba(0,0,0,0.12); }
.ceo.theme-light .cadence__when { color:#0a6bd4; }
.ceo.theme-light .cadence__count { color:rgba(0,0,0,0.45); }
.ceo.theme-light .caprow { background:#fff; border:1px solid rgba(0,0,0,0.12); box-shadow:0 4px 12px rgba(0,0,0,0.06); }
.ceo.theme-light .caprow--on { background:linear-gradient(155deg, rgba(48,209,88,0.10), #fff 70%); border-color:rgba(48,209,88,0.45); }
.ceo.theme-light .caprow__label { color:#0e1420; }
.ceo.theme-light .caprow__detail { color:rgba(0,0,0,0.55); }
.ceo.theme-light .caprow__status { background:rgba(0,0,0,0.06); color:rgba(0,0,0,0.5); }
.ceo.theme-light .caprow__status.ready { background:rgba(48,209,88,0.16); color:#1a7f37; }
.ceo .agent-pick__group:first-child { margin-top:0; }
.ceo.theme-light .agent-pick__group { color:#0a6bd4; background:linear-gradient(180deg, rgba(255,255,255,0.97), rgba(255,255,255,0.85)); border-bottom:1px solid rgba(0,0,0,0.10); }
.ceo .agent-pick__detail { display:block; margin-top:2px; font-size:12px; color:var(--text-2); }
.ceo .agent-setup__review { margin-top:14px; display:flex; flex-direction:column; gap:8px; }
.ceo .agent-setup__foot { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:18px; }
.ceo .agent-setup__next { width:auto; margin-top:0; padding:12px 26px; }
.ceo .agent-setup__activate:disabled { opacity:0.4; cursor:not-allowed; transform:none; box-shadow:none; }
.ceo .agent-setup__back { font:inherit; font-size:14px; font-weight:600; padding:12px 18px; border:none; border-radius:12px; cursor:pointer; background:rgba(255,255,255,0.07); color:var(--text-2); }
.ceo .agent-setup__back:hover { background:rgba(255,255,255,0.13); color:#fff; }

.ceo.theme-light .agent-setup__step { background:rgba(0,0,0,0.12); }
.ceo.theme-light .agent-field__input { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
.ceo.theme-light .agent-field__label { color:#5c5c63; }
.ceo.theme-light .agent-pick { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
.ceo.theme-light .agent-pick.on { border-color:#0a63ff; background:rgba(10,99,208,0.10); }
.ceo.theme-light .agent-pick__detail { color:#5c5c63; }
.ceo.theme-light .agent-pick__check { background:rgba(0,0,0,0.10); }
.ceo.theme-light .agent-pick__check.on { background:#0a63ff; }
.ceo.theme-light .agent-setup__back { background:rgba(0,0,0,0.06); color:#5c5c63; }

/* ── Drill-down: tap a number → the list behind it ──────────────────────── */
.ceo .ceo-drill { position:fixed; inset:0; z-index:1100; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(8,8,12,0.30); backdrop-filter:blur(10px) saturate(115%); -webkit-backdrop-filter:blur(10px) saturate(115%); -webkit-app-region:no-drag; app-region:no-drag; }
.ceo .ceo-drill__panel { position:relative; width:100%; max-width:480px; max-height:84vh; overflow-y:auto; padding:24px 24px 18px; border-radius:22px; background:#17171c; border:1px solid rgba(255,255,255,0.10); box-shadow:0 30px 80px rgba(0,0,0,0.55); color:var(--text); }
.ceo .ceo-drill__close { position:absolute; top:14px; right:52px; width:30px; height:30px; border:none; border-radius:9px; background:rgba(255,255,255,0.08); color:var(--text-2); font-size:18px; line-height:1; cursor:pointer; }
.ceo .ceo-drill__close:hover { background:rgba(255,255,255,0.16); color:#fff; }
.ceo .ceo-drill__title { font-size:20px; font-weight:800; letter-spacing:-0.01em; }
.ceo .ceo-drill__sub { margin-top:4px; font-size:12px; color:var(--text-2); }
.ceo .ceo-drill__total { margin-top:14px; font-size:34px; font-weight:800; letter-spacing:-0.02em; font-variant-numeric:tabular-nums; }
.ceo .ceo-drill__list { margin-top:16px; display:flex; flex-direction:column; gap:8px; }
.ceo .ceo-drill__empty { font-size:13px; color:var(--text-3); text-align:center; padding:26px 0; }
.ceo .ceo-drill__row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:11px 13px; border-radius:12px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); }
.ceo .ceo-drill__rmain { min-width:0; }
.ceo .ceo-drill__rtitle { font-size:13.5px; font-weight:600; color:var(--text); }
.ceo .ceo-drill__rmeta { margin-top:4px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.ceo .ceo-drill__tag { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; padding:2px 7px; border-radius:999px; background:rgba(255,255,255,0.08); color:var(--text-2); }
.ceo .ceo-drill__tag--completed { background:rgba(48,209,88,0.16); color:var(--c-ok); }
.ceo .ceo-drill__tag--drafted { background:rgba(255,214,10,0.16); color:var(--c-warn); }
.ceo .ceo-drill__tag--invoiced { background:rgba(10,132,255,0.16); color:var(--c-cyan); }
.ceo .ceo-drill__date { font-size:11.5px; color:var(--text-2); }
.ceo .ceo-drill__status { font-size:11px; color:var(--text-3); text-transform:capitalize; }
.ceo .ceo-drill__amt { font-size:15px; font-weight:700; font-variant-numeric:tabular-nums; white-space:nowrap; }
.ceo.theme-light .ceo-drill__panel { background:#fff; border-color:rgba(0,0,0,0.10); color:#1d1d1f; box-shadow:0 30px 80px rgba(0,0,0,0.30); }
.ceo.theme-light .ceo-drill__row { background:#f5f6f9; border-color:rgba(0,0,0,0.06); }
.ceo.theme-light .ceo-drill__close { background:rgba(0,0,0,0.06); color:#5c5c63; }
.ceo.theme-light .ceo-drill__sub, .ceo.theme-light .ceo-drill__date { color:#5c5c63; }

/* ── Agent boxes: title only · "Set up" (off) vs blue (active) ──────────── */
.ceo .agent2 { display:flex; align-items:center; justify-content:space-between; gap:14px; width:100%; text-align:left; font-family:inherit; padding:16px 20px; border-radius:16px; transition:transform .25s cubic-bezier(0.2,0.8,0.2,1), border-color .25s, box-shadow .25s; }
.ceo .agent2--off { background:linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)); border:0.5px solid rgba(255,255,255,0.10); }
.ceo .agent2--on { cursor:pointer; background:linear-gradient(155deg, rgba(10,132,255,0.16), rgba(10,132,255,0.04) 70%), rgba(8,12,20,0.5); border:0.5px solid rgba(10,132,255,0.45); box-shadow:inset 0 1px 0 rgba(255,255,255,0.12), 0 0 24px rgba(10,132,255,0.12); }
.ceo .agent2--on:hover { transform:translateY(-2px); border-color:rgba(10,132,255,0.7); box-shadow:inset 0 1px 0 rgba(255,255,255,0.16), 0 0 32px rgba(10,132,255,0.22), 0 12px 28px rgba(0,0,0,0.45); }
.ceo .agent2__title { font-size:15px; font-weight:600; color:var(--text); letter-spacing:-0.005em; }
.ceo .agent2--on .agent2__title { color:#fff; }
.ceo .agent2__actions { display:flex; align-items:center; gap:8px; flex-shrink:0; }
.ceo .agent2__more { font:inherit; font-size:12.5px; font-weight:600; color:var(--text-2); background:transparent; border:0.5px solid rgba(255,255,255,0.16); border-radius:999px; padding:6px 13px; cursor:pointer; transition:color .15s, border-color .15s; }
.ceo .agent2__more:hover { color:var(--text); border-color:rgba(255,255,255,0.32); }
.ceo { position:relative; }
.ceo .today-big-title { color: var(--brandA, #0a63ff) !important; }
.ceo .today-kicker { color: var(--brandA, inherit); }
.ceo .agent2__setup { color: var(--brandA, #0a63ff); }
.ceo.theme-light .agent2__setup { background: var(--brandA, #0a63ff) !important; color:#fff !important; }
.ceo .agent2__morelink { color: var(--brandA, #0a63ff); }
/* ── THE BOOKS (8/13) ─────────────────────────────────────────────────────
 * Braeden: "make it look how the list looked when it was in books" — the same
 * deck-slide row he signed off on 8/10 for the software stack: WHITE rows,
 * heavy near-black label left, muted detail centre, the number blue on the
 * right, generous padding, 18px radius, lift on hover. Identical language,
 * only the label size is stepped down because a ledger runs many rows deep
 * where that list ran six. */
/* Systems opens over the page — same soft ground and white rows as the list
   it came from, so it reads as the SAME thing, just bigger (8/13). */
.sysview { position:fixed; inset:0; z-index:1400; background:rgba(8,10,16,.55);
  backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
  display:flex; align-items:flex-start; justify-content:center; padding:4vh 16px; overflow:auto; }
.sysview__panel { width:min(1000px,100%); background:#cfd8e8; border-radius:22px; padding:26px clamp(18px,3vw,34px) 34px;
  box-shadow:0 24px 70px rgba(0,0,0,.4); }
.ceo.theme-light .sysview__panel { background:#e8edf5; }
.sysview__head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:18px; }
.sysview__title { font-size:clamp(28px,3.4vw,44px); font-weight:800; letter-spacing:-.03em; color:#0b1020; line-height:1.05; }
.sysview__sub { color:#5b6472; font-weight:500; font-size:clamp(13px,1.1vw,16px); margin-top:4px; }
.sysview__close { background:#fff; border:0; width:38px; height:38px; border-radius:50%; font-size:22px; line-height:1;
  color:#0b1020; cursor:pointer; box-shadow:0 1px 2px rgba(16,32,64,.10); flex:0 0 auto; }
.sysview__close:hover { box-shadow:0 3px 10px rgba(16,32,64,.16); }
/* inside the panel the books always use the light card treatment */
.sysview .books__tile, .sysview .books__row, .sysview .books__filter { background:#fff !important; box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05) !important; }
.sysview .books__tile b, .sysview .books__title, .sysview .books__title em { color:#0b1020 !important; }
.sysview .books__tile span, .sysview .books__detail { color:#5b6472 !important; }
.sysview .books__filter--on { background:#0b1020 !important; color:#fff !important; }
.sysview .books__amt { color:#1668ff !important; }
.sysview .books__amt--out { color:#c2410c !important; }

.ceo .ceo-today--system { background:none; padding:26px 0 30px; }
.ceo .books__tiles { display:flex; flex-wrap:wrap; gap:10px; margin:16px 0 18px; }
.ceo .books__tile { background:#fff; border-radius:14px; padding:14px 18px; min-width:120px;
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05); }
.ceo .books__tile b { display:block; font-size:1.35rem; font-weight:800; letter-spacing:-.03em; color:#0b1020; line-height:1.1; }
.ceo .books__tile span { font-size:.8rem; font-weight:500; color:#5b6472; }
.ceo .books__filters { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.ceo .books__filter { background:#fff; border:0; color:#5b6472; border-radius:999px; padding:8px 16px;
  font-size:.9rem; font-weight:600; cursor:pointer; box-shadow:0 1px 2px rgba(16,32,64,.06);
  transition:transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s cubic-bezier(.16,1,.3,1); }
.ceo .books__filter:hover { transform:translateY(-1px); box-shadow:0 2px 6px rgba(16,32,64,.10); }
.ceo .books__filter--on { background:#0b1020; color:#fff; }
.ceo .books__list { display:flex; flex-direction:column; gap:14px; }
/* the 8/10 row, verbatim: label | detail | number */
.ceo .books__row { display:grid; grid-template-columns:minmax(0,auto) 1fr auto; align-items:center;
  gap:clamp(14px,3vw,40px); width:100%; box-sizing:border-box; text-align:left;
  background:#fff; border:0; border-radius:18px; padding:20px clamp(18px,2.6vw,34px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05);
  transition:transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s cubic-bezier(.16,1,.3,1); }
.ceo .books__row:hover { transform:translateY(-2px); box-shadow:0 2px 4px rgba(16,32,64,.07), 0 14px 30px rgba(16,32,64,.10); }
.ceo .books__title { font-weight:800; letter-spacing:-.03em; line-height:1.08;
  font-size:clamp(17px,1.5vw,23px); color:#0b1020; min-width:0; }
.ceo .books__title em { font-style:normal; font-weight:800; color:#0b1020; }
.ceo .books__detail { color:#5b6472; font-weight:500; font-size:clamp(13px,1.05vw,16px); line-height:1.35; min-width:0; }
.ceo .books__detail .books__when { color:#93a3b8; }
.ceo .books__amt { justify-self:end; white-space:nowrap; font-weight:700; font-size:clamp(15px,1.2vw,19px); color:#1668ff; }
.ceo .books__amt--out { color:#c2410c; }
.ceo .books__empty { color:#5b6472; font-size:1rem; padding:22px 2px; }
/* night: keep the same shape, just a dark card so it isn't a white slab */
.ceo:not(.theme-light) .books__tile,
.ceo:not(.theme-light) .books__row,
.ceo:not(.theme-light) .books__filter { background:#12161f; box-shadow:0 1px 2px rgba(0,0,0,.4); }
.ceo:not(.theme-light) .books__tile b,
.ceo:not(.theme-light) .books__title,
.ceo:not(.theme-light) .books__title em { color:#eef2f8; }
.ceo:not(.theme-light) .books__tile span,
.ceo:not(.theme-light) .books__detail { color:#93a3b8; }
.ceo:not(.theme-light) .books__filter--on { background:#eef2f8; color:#0b1020; }
.ceo:not(.theme-light) .books__amt { color:#4d8dff; }
.ceo:not(.theme-light) .books__amt--out { color:#ff8a5b; }
@media (max-width:680px) {
  .ceo .books__row { grid-template-columns:1fr; gap:6px; padding:16px 18px 18px; }
  .ceo .books__amt { justify-self:start; }
}

/* Recommended software section is BLUE regardless of brand accent (owner
   spec 2026-07-14 — HowTo's gold brand was tinting it yellow). Both themes. */
.ceo .ceo-today--software .today-big-title { color:#0a84ff !important; }
.ceo .ceo-today--software .today-kicker { color:#0a84ff; }
.ceo .ceo-today--software .agent2__setup { color:#0a84ff; }
.ceo .ceo-today--software .agent2__morelink { color:#0a84ff; }
.ceo.theme-light .ceo-today--software .today-big-title { color:#0a63ff !important; }
.ceo.theme-light .ceo-today--software .agent2__setup { background:#0a63ff !important; color:#fff !important; }
.ceo.theme-light .ceo-today--software .agent2__morelink { color:#0a63ff; }

/* ── Braeden 8/10: the Recommended-software stack is restyled to the deck-slide
   row he sent — white rows on a soft blue ground, heavy black label left, muted
   detail centre, blue action right. Scoped to --software so no other agent list
   on the page changes. */
/* no ground behind the stack — owner 8/10: "get rid of the dumb ass background" */
.ceo .ceo-today--software { background:none; padding:26px 0 30px; }
.ceo .ceo-today--software .biglist { display:flex; flex-direction:column; gap:14px; }
.ceo .ceo-today--software .bigrow {
  display:grid; grid-template-columns:minmax(0,auto) 1fr auto; align-items:center;
  gap:clamp(14px,3vw,40px); width:100%; box-sizing:border-box; text-align:left;
  background:#fff; border:0; border-radius:18px; padding:22px clamp(18px,2.6vw,34px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05); cursor:pointer;
  transition:transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s cubic-bezier(.16,1,.3,1); }
.ceo .ceo-today--software .bigrow:hover {
  transform:translateY(-2px); box-shadow:0 2px 4px rgba(16,32,64,.07), 0 14px 30px rgba(16,32,64,.10); }
.ceo .ceo-today--software .bigrow__label {
  font-weight:800; letter-spacing:-.03em; line-height:1.05;
  font-size:clamp(21px,2.3vw,34px); color:#0b1020; }
.ceo .ceo-today--software .bigrow__detail {
  color:#5b6472; font-weight:500; font-size:clamp(14px,1.15vw,17px); line-height:1.35; min-width:0; }
.ceo .ceo-today--software .bigrow__go {
  justify-self:end; white-space:nowrap; font-weight:700;
  font-size:clamp(14px,1.15vw,17px); color:#1668ff;
  display:inline-flex; align-items:center; gap:10px; }
/* "Set up · 5" — a separate target on the row. The row itself always opens the
   thing; this only offers the questions (8/13). */
.ceo .ceo-today--software .bigrow__setup {
  font-size:11px; font-weight:800; letter-spacing:.05em; text-transform:uppercase;
  padding:5px 11px; border-radius:999px; background:rgba(22,104,255,.10);
  color:#1668ff; border:1px solid rgba(22,104,255,.28); cursor:pointer; }
.ceo .ceo-today--software .bigrow__setup:hover { background:#1668ff; color:#fff; border-color:#1668ff; }
.ceo:not(.theme-light) .ceo-today--software .bigrow__setup {
  background:rgba(77,141,255,.14); color:#4d8dff; border-color:rgba(77,141,255,.34); }
@media (max-width:680px) {
  .ceo .ceo-today--software .bigrow { grid-template-columns:1fr; gap:7px; padding:18px 18px 20px; }
  .ceo .ceo-today--software .bigrow__go { justify-self:start; }
}
.ceo .ceo-today--software .task-list { display:flex; flex-direction:column; gap:14px; }
.ceo .ceo-today--software .agent2 {
  display:grid; grid-template-columns:minmax(0,auto) 1fr auto; align-items:center;
  gap:clamp(14px,3vw,40px); width:100%; box-sizing:border-box; text-align:left;
  background:#fff; border:0; border-radius:18px; padding:22px clamp(18px,2.6vw,34px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05); cursor:pointer;
  transition:transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s cubic-bezier(.16,1,.3,1); }
.ceo .ceo-today--software .agent2:hover {
  transform:translateY(-2px); box-shadow:0 2px 4px rgba(16,32,64,.07), 0 14px 30px rgba(16,32,64,.10); }
.ceo .ceo-today--software .agent2__title {
  font-weight:800; letter-spacing:-.03em; line-height:1.05;
  font-size:clamp(21px,2.3vw,34px); color:#0b1020; }
.ceo .ceo-today--software .agent2__detail {
  color:#5b6472; font-weight:500; font-size:clamp(14px,1.15vw,17px); line-height:1.35; }
/* both states read as one blue text link on the right */
.ceo .ceo-today--software .agent2__actions { display:flex; align-items:center; gap:18px; justify-self:end; }
.ceo .ceo-today--software .agent2__active { justify-self:end; }
.ceo .ceo-today--software .agent2__setup,
.ceo .ceo-today--software .agent2__morelink,
.ceo .ceo-today--software .agent2__active {
  white-space:nowrap; background:none !important; border:0; padding:0;
  font-weight:700; font-size:clamp(14px,1.15vw,17px); color:#1668ff !important; }
.ceo.theme-light .ceo-today--software .agent2__setup { background:none !important; color:#1668ff !important; }
.ceo .ceo-today--software .agent2__title, .ceo .ceo-today--software .agent2__detail { min-width:0; }
/* phone: stack the three columns, keep the action left-aligned under the label */
@media (max-width:680px) {
  .ceo .ceo-today--software .agent2 { grid-template-columns:1fr; gap:7px; padding:18px 18px 20px; }
  .ceo .ceo-today--software .agent2__actions,
  .ceo .ceo-today--software .agent2__active { justify-self:start; }
}

.ceo .ceo-hero--brand .pt-h { text-shadow: 0 2px 18px var(--brandB, transparent); }
.ceo > * { position:relative; z-index:1; }
.ceo .ceo-banner img { flex:1; min-width:0; object-fit:cover; }

.ceo .ceo-brandbg { position:absolute; top:0; bottom:0; left:50%; transform:translateX(-50%); width:100vw; z-index:-1; pointer-events:none; }
.ceo .ceo-polaroid { position:absolute; width:auto; max-width:var(--wallW, clamp(230px, 34vh, 470px)); padding:0; background:none; border-radius:10px; box-shadow:0 8px 22px rgba(0,0,0,.4); display:block; }
.ceo .ceo-polaroid .tp { position:absolute; width:68px; height:24px; z-index:2; /* owner 2026-07-15: bigger tape */
  background:repeating-linear-gradient(100deg, #1a73e8 0 6px, #1557b0 6px 10px); /* owner: tape stays BLUE for every brand */
  border-radius:3px; box-shadow:0 2px 5px rgba(0,0,0,.35); }
/* Center each strip ON its corner (translate to the corner point) so it straddles
   it like real tape — half on the photo, half off — instead of floating outside. */
.ceo .ceo-polaroid .tp1 { top:0; left:0; transform:translate(-50%,-50%) rotate(-45deg); }
.ceo .ceo-polaroid .tp2 { top:0; right:0; transform:translate(50%,-50%) rotate(45deg); }
.ceo .ceo-polaroid .tp3 { bottom:0; left:0; transform:translate(-50%,50%) rotate(45deg); }
.ceo .ceo-polaroid .tp4 { bottom:0; right:0; transform:translate(50%,50%) rotate(-45deg); }
.ceo .ceo-polaroid img { max-width:var(--wallW, clamp(230px, 34vh, 470px)); max-height:var(--wallH, 44vh); width:auto; height:auto; display:block; border-radius:10px; border:8px solid #fff; box-sizing:border-box; }
/* The side photos must live ENTIRELY in the gutter beside the centered content
   column, never reaching the buttons/tiles. --gutter = the real space on each
   side; cap the photo (incl. its tape ±20px) to it and pin them to the very edge
   so overlap is impossible at any screen size. */
.ceo { --gutter: calc((100vw - min(1500px, 72vw)) / 2); }
.ceo .ceo-polaroid { max-width:calc(var(--gutter) - 58px); }
.ceo .ceo-polaroid img { max-width:calc(var(--gutter) - 58px); }
/* Spread the photo wall down the whole viewport (they were clumped low). */
.ceo .pp0 { right:10px; top:42vh; transform:rotate(5deg); }
.ceo .pp1 { right:10px; top:102vh; transform:rotate(-3deg); }
.ceo .pp2 { right:10px; top:162vh; transform:rotate(2deg); }
.ceo .pp3 { left:10px; top:72vh; transform:rotate(-4deg); }
.ceo .pp4 { left:10px; top:132vh; transform:rotate(3deg); }
.ceo .pp5 { left:10px; top:192vh; transform:rotate(-2deg); }
/* Side flourishes (photo wall + big logo badge) live in the gutters beside the
   centered 72vw content column, so each gutter is ~14vw. Below 1500px the photo
   wall needs more room than that leaves — retire it. The logo badge shrinks to
   fit inside the gutter (capped at 12vw so it can NEVER cross into content,
   whatever the window height), then hides once the window is too small to hold
   it cleanly. This keeps the layout clean at every size. */
@media (max-width: 1700px) {
  .ceo .ceo-polaroid, .ceo .ceo-brandbg__logo { display:none; }
  .ceo .ceo-avatar { width:min(26vh, 12vw); height:min(26vh, 12vw); margin-left:calc(-50vw + 0.8vw); }
}
@media (max-width: 1180px) {
  .ceo .ceo-avatar { display:none; }
}
/* Pre-connect page is shorter (sections gated) - the deepest wall photos would
   float in empty black past the content, so they wait for the full layout */
.ceo.ceo--precal .pp2, .ceo.ceo--precal .pp5 { display:none; }
.ceo.ceo--precal { min-height:100vh; }
@media (min-width: 1500px) {
  .ceo .wrap { max-width:min(1500px, 72vw); }
  .ceo .task-list { max-width:min(1100px, 56vw); }
  .ceo .cadence { max-width:min(1100px, 56vw); }   /* headings track their rows */
  .ceo .agent-hero, .ceo .agent-setupwrap { max-width:min(1100px, 56vw); }
  .ceo .ceo-cal-portal { width:min(1820px, 95vw); height:900px; }
  .ceo .bub-value { font-size:44px; }
  .ceo .bubs { gap:22px; }
}
.ceo .ceo-brandbg__logo { position:absolute; right:1.6vw; bottom:3vh; width:110px; height:110px; border-radius:50%; object-fit:cover; box-shadow:0 6px 18px rgba(0,0,0,.4); }
.ceo .ceo-cal-portal { position:relative; left:50%; transform:translateX(-50%); margin:18px 0 6px; width:min(1620px, 96vw); height:820px; min-height:0; border-radius:18px; overflow:hidden; background:#131722; box-shadow:0 10px 34px rgba(0,0,0,.45); display:flex; flex-direction:column; }
.ceo.theme-light .ceo-cal-portal { background:#fff; box-shadow:0 10px 30px rgba(16,24,40,.18); border:1px solid #e3e8ef; }
.ceo .ceo-cal-portal__bar { display:flex; align-items:center; justify-content:space-between; padding:6px 16px 9px; background:#f8f9fa; border-bottom:1px solid #e3e8ef; color:#1f2937; flex-wrap:wrap; }
.ceo .ceo-cal-portal__title { flex:1; text-align:center; font-weight:800; font-size:clamp(19px, 1.5vw, 27px); color:#0a63ff; letter-spacing:.4px; margin:0; }
.ceo .ceo-cal-portal webview, .ceo .ceo-cal-portal iframe { flex:1; }
.ceo .ceo-hero--brand { position:relative; }
.ceo .ceo-hero__backdrop img { flex:1; object-fit:cover; min-width:0; }
.ceo .agent2__reco { display:block; font-size:10px; font-weight:800; letter-spacing:1.2px; text-transform:uppercase; color:#7f8ba3; margin-bottom:2px; }
.ceo .agent2__setup { font:inherit; font-size:12.5px; font-weight:700; color:#0a63ff; background:#fff; border:none; border-radius:999px; padding:7px 15px; cursor:pointer; transition:filter .15s; }
.ceo .agent2__setup:hover { filter:brightness(0.94); }
.ceo .agent2__titlewrap { display:inline-flex; align-items:center; gap:11px; min-width:0; }
.ceo .agent2__morelink { font:inherit; font-size:12.5px; font-weight:600; color:#0a84ff; background:transparent; border:none; padding:0; cursor:pointer; white-space:nowrap; }
.ceo .agent2__morelink:hover { text-decoration:underline; }
.ceo.theme-light .agent2__morelink { color:#0a63ff; }
.ceo .agent-setupwrap { max-width:720px; margin:20px auto 0; display:flex; justify-content:center; }
.ceo .agent-setup-cta { font:inherit; font-size:14px; font-weight:700; color:#fff; background:linear-gradient(160deg, #0a84ff 0%, #0a5fd0 100%); border:none; border-radius:12px; padding:12px 28px; cursor:pointer; box-shadow:0 8px 22px rgba(0,60,160,0.35); transition:transform .12s; }
.ceo .agent-setup-cta:hover { transform:translateY(-1px); }

/* ── Full-page agent views (Scheduling, Leads) ──────────────────────────── */
/* z-index 1050 (was 50): SchedulingDetail opens OVER the ScheduleBoard (z 1000) via
   "Google Calendar setup" — at 50 it rendered invisibly UNDER the board, so its Back
   button could never be clicked. Still below ceo-drill/agent-detail popups (1100). */
.ceo .agent-page { position:fixed; inset:0; z-index:1050; overflow-y:auto; background:linear-gradient(180deg, #1a1b22 0%, #101117 32%, #0a0a10 70%, #08080d 100%); -webkit-app-region:no-drag; app-region:no-drag; }
.ceo.theme-light .agent-page { background:linear-gradient(180deg, #e9ebf0 0%, #f2f3f7 42%, #fbfbfd 100%); }
.ceo .agent-page__inner { max-width:1280px; margin:0 auto; padding:60px 40px 90px; }
.ceo .agent-page__back { display:inline-flex; align-items:center; gap:7px; font:inherit; font-size:13.5px; font-weight:600; color:var(--text-2); background:transparent; border:none; cursor:pointer; padding:6px 0; }
.ceo .agent-page__back svg { width:18px; height:18px; }
.ceo .agent-page__back:hover { color:var(--text); }
.ceo .agent-page__title { font-size:30px; font-weight:800; letter-spacing:-0.02em; margin:8px 0 4px; color:var(--text); }
.ceo .agent-page__sub { font-size:13.5px; color:var(--text-2); margin-bottom:18px; }
.ceo .agent-page__section { margin:24px 0 12px; font-size:12px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-3); }
.ceo .agent-page__card { background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:16px; padding:16px 18px; }
.ceo.theme-light .agent-page__card { background:#fff; border-color:rgba(0,0,0,0.08); box-shadow:0 4px 14px rgba(0,0,0,0.06); }
.ceo .aswitch { position:relative; width:46px; height:27px; flex:none; display:inline-block; }
.ceo .aswitch input { position:absolute; inset:0; opacity:0; margin:0; cursor:pointer; z-index:2; }
.ceo .aswitch__track { position:absolute; inset:0; border-radius:999px; background:rgba(255,255,255,0.20); transition:background .2s; }
.ceo .aswitch input:checked ~ .aswitch__track { background:#30d158; }
.ceo .aswitch__knob { position:absolute; top:3px; left:3px; width:21px; height:21px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,0.35); transition:transform .2s; }
.ceo .aswitch input:checked ~ .aswitch__knob { transform:translateX(19px); }
.ceo.theme-light .aswitch__track { background:rgba(0,0,0,0.18); }
.ceo .lead-sources { display:flex; flex-direction:column; gap:10px; }
.ceo .lead-source { display:flex; align-items:center; justify-content:space-between; gap:14px; padding:14px 16px; border-radius:14px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); }
.ceo.theme-light .lead-source { background:#fff; border-color:rgba(0,0,0,0.08); box-shadow:0 4px 12px rgba(0,0,0,0.05); }
.ceo .lead-source__name { font-size:15px; font-weight:600; color:var(--text); }
.ceo .lead-source__count { font-size:12.5px; color:var(--text-2); margin-top:2px; }
.ceo .agent2__active { display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:700; color:#cfe4ff; }
.ceo .agent2__dot { width:7px; height:7px; border-radius:50%; background:#0a84ff; box-shadow:0 0 10px #0a84ff; animation:ceoDot 2.4s ease-in-out infinite; }
.ceo.theme-light .agent2--off { background:#fff; border:1px solid rgba(0,0,0,0.12); box-shadow:0 4px 12px rgba(0,0,0,0.06); }
.ceo.theme-light .agent2--on { background:linear-gradient(160deg, #3a9bff 0%, #0a63ff 100%); border:1px solid rgba(0,0,0,0.45); box-shadow:0 6px 16px rgba(0,0,0,0.16); }
.ceo.theme-light .agent2--on:hover { box-shadow:0 16px 34px rgba(0,60,160,0.4); }
.ceo.theme-light .agent2--on .agent2__title, .ceo.theme-light .agent2__active { color:#fff; }
.ceo.theme-light .agent2__more { color:#5c5c63; border-color:rgba(0,0,0,0.18); }
.ceo.theme-light .agent2__more:hover { color:#1d1d1f; border-color:rgba(0,0,0,0.32); }
.ceo.theme-light .agent2__setup { background:#0a63ff; color:#fff; }
.ceo.theme-light .agent2__reco { color:#64748b; }

/* ── Agent detail / setup panel ─────────────────────────────────────────── */
.ceo .agent-detail { position:fixed; inset:0; z-index:1100; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(8,8,12,0.30); backdrop-filter:blur(10px) saturate(115%); -webkit-backdrop-filter:blur(10px) saturate(115%); -webkit-app-region:no-drag; app-region:no-drag; }
.ceo .agent-detail__panel { position:relative; width:100%; max-width:460px; max-height:86vh; overflow-y:auto; padding:26px 24px 22px; border-radius:22px; background:#17171c; border:1px solid rgba(255,255,255,0.10); box-shadow:0 30px 80px rgba(0,0,0,0.55); color:var(--text); }
.ceo .agent-detail__close { position:absolute; top:14px; right:52px; width:30px; height:30px; border:none; border-radius:9px; background:rgba(255,255,255,0.08); color:var(--text-2); font-size:18px; line-height:1; cursor:pointer; }
.ceo .agent-detail__close:hover { background:rgba(255,255,255,0.16); color:#fff; }
.ceo .agent-detail__title { font-size:21px; font-weight:800; letter-spacing:-0.01em; }
.ceo .agent-detail__cadence { margin-top:4px; font-size:11px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:#0a84ff; }
.ceo .agent-detail__desc { margin-top:14px; font-size:14px; line-height:1.55; color:var(--text-2); }
.ceo .agent-detail__section { margin:18px 0 8px; font-size:11px; font-weight:700; letter-spacing:1.4px; text-transform:uppercase; color:var(--text-3); }
.ceo .agent-detail__list { margin:0; padding-left:18px; display:flex; flex-direction:column; gap:6px; }
.ceo .agent-detail__list li { font-size:13.5px; color:var(--text); }
.ceo .agent-detail__cta { margin-top:20px; width:100%; padding:14px; border:none; border-radius:13px; cursor:pointer; font:inherit; font-size:15px; font-weight:800; color:#fff; background:linear-gradient(160deg, #0a84ff 0%, #0a5fd0 100%); box-shadow:0 8px 22px rgba(0,60,160,0.40); transition:transform .12s; }
.ceo .agent-detail__cta:hover { transform:translateY(-1px); }
.ceo .agent-detail__status { margin-top:20px; padding:13px; border-radius:12px; text-align:center; font-size:14px; font-weight:700; color:var(--c-ok); background:rgba(48,209,88,0.12); border:1px solid rgba(48,209,88,0.30); }
.ceo .agent-detail__note { margin-top:12px; font-size:11.5px; color:var(--text-3); text-align:center; }
.ceo.theme-light .agent-detail__panel { background:#fff; border-color:rgba(0,0,0,0.10); color:#1d1d1f; box-shadow:0 30px 80px rgba(0,0,0,0.30); }
.ceo.theme-light .agent-detail__close { background:rgba(0,0,0,0.06); color:#5c5c63; }
.ceo.theme-light .agent-detail__desc { color:#5c5c63; }
.ceo.theme-light .agent-detail__list li { color:#1d1d1f; }
/* Scheduling panel bits */
.ceo .sched-muted { font-size:13px; color:var(--text-2); line-height:1.5; }
.ceo .sched-warn { font-size:12.5px; line-height:1.5; color:var(--c-warn); background:rgba(255,214,10,0.10); border:1px solid rgba(255,214,10,0.28); border-radius:10px; padding:11px 13px; }
.ceo .sched-ok { font-size:13px; font-weight:600; color:var(--c-ok); }
.ceo .sched-refresh { margin-top:10px; width:100%; padding:10px; border:0.5px solid rgba(255,255,255,0.16); border-radius:11px; background:transparent; color:var(--text-2); font:inherit; font-size:13px; cursor:pointer; }
.ceo .sched-refresh:hover { color:var(--text); border-color:rgba(255,255,255,0.3); }
.ceo .sched-events { margin-top:12px; display:flex; flex-direction:column; gap:7px; }
.ceo .sched-event { padding:9px 11px; border-radius:10px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); }
.ceo .sched-event__t { font-size:13px; font-weight:600; color:var(--text); }
.ceo .sched-event__m { font-size:11.5px; color:var(--text-2); margin-top:2px; }
.ceo .sched-link { display:inline-block; margin-top:12px; font-size:12.5px; color:#0a84ff; text-decoration:none; }
.ceo .sched-toggle { display:flex; align-items:center; gap:10px; font-size:13.5px; color:var(--text); cursor:pointer; }
.ceo .sched-toggle input { width:16px; height:16px; flex:none; }
.ceo .sched-leadrow { margin-top:10px; display:flex; align-items:center; gap:10px; }
.ceo .sched-leadlabel { font-size:12.5px; color:var(--text-2); }
.ceo .sched-select { font:inherit; font-size:13px; padding:7px 10px; border-radius:9px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.14); color:var(--text); }
.ceo.theme-light .sched-event { background:#f5f6f9; border-color:rgba(0,0,0,0.06); }
.ceo.theme-light .sched-muted, .ceo.theme-light .sched-leadlabel { color:#5c5c63; }
.ceo.theme-light .sched-select { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
.ceo.theme-light .sched-refresh { color:#5c5c63; border-color:rgba(0,0,0,0.18); }

/* ── Middleman notification bell + inbox ────────────────────────────────── */
.ceo .ceo-bell { position:relative; display:inline-flex; align-items:center; justify-content:center; width:40px; height:32px; border-radius:999px; background:rgba(255,255,255,0.05); border:0.5px solid rgba(255,255,255,0.08); color:var(--text-2); cursor:pointer; transition:color .15s, border-color .15s; }
.ceo .ceo-bell:hover { color:var(--text); border-color:rgba(255,255,255,0.22); }
.ceo .ceo-bell svg { width:17px; height:17px; }
.ceo .ceo-bell__badge { position:absolute; top:-5px; right:-5px; min-width:17px; height:17px; padding:0 4px; border-radius:999px; background:#ff453a; color:#fff; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; box-shadow:0 0 0 2px var(--bg-deep); }
.ceo .notif-list { margin-top:14px; display:flex; flex-direction:column; gap:8px; max-height:54vh; overflow-y:auto; }
.ceo .notif-row { display:flex; align-items:flex-start; gap:11px; width:100%; text-align:left; font-family:inherit; padding:11px 12px; border-radius:12px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); cursor:pointer; transition:border-color .15s; }
.ceo .notif-row:hover { border-color:rgba(255,255,255,0.22); }
.ceo .notif-row--unread { background:rgba(10,132,255,0.08); border-color:rgba(10,132,255,0.28); }
.ceo .notif-ic { font-size:18px; flex:none; line-height:1.25; }
.ceo .notif-main { display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
.ceo .notif-title { font-size:13.5px; font-weight:600; color:var(--text); display:flex; align-items:center; gap:8px; }
.ceo .notif-urgent { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:#ff453a; background:rgba(255,69,58,0.16); padding:1px 6px; border-radius:999px; }
.ceo .notif-body { font-size:12px; color:var(--text-2); line-height:1.4; }
.ceo .notif-meta { font-size:11px; color:var(--text-3); text-transform:capitalize; }
.ceo .notif-dot { width:8px; height:8px; border-radius:50%; background:#0a84ff; flex:none; margin-top:5px; }
.ceo .notif-readall { margin-top:12px; width:100%; padding:10px; border:0.5px solid rgba(255,255,255,0.16); border-radius:11px; background:transparent; color:var(--text-2); font:inherit; font-size:13px; cursor:pointer; }
.ceo .notif-readall:hover { color:var(--text); border-color:rgba(255,255,255,0.3); }
.ceo.theme-light .ceo-bell { background:#fff; border-color:rgba(0,0,0,0.12); color:#5c5c63; }
.ceo.theme-light .ceo-bell:hover { color:#1d1d1f; }
.ceo.theme-light .ceo-bell__badge { box-shadow:0 0 0 2px #fff; }
.ceo.theme-light .notif-row { background:#f5f6f9; border-color:rgba(0,0,0,0.06); }
.ceo.theme-light .notif-row--unread { background:rgba(10,99,208,0.08); border-color:rgba(10,99,208,0.28); }

/* ── Aria "Want some help?" intro on a setup page ───────────────────────── */
.ceo .aria-help { display:flex; align-items:flex-start; gap:12px; margin:8px 0 4px; padding:14px 16px; border-radius:16px; background:rgba(0,217,255,0.07); border:1px solid rgba(0,217,255,0.24); }
.ceo .aria-help__dot { flex:none; width:26px; height:26px; border-radius:50%; margin-top:2px; background:radial-gradient(circle at 35% 30%, #6ff0ff 0%, #00d9ff 45%, #0a84ff 100%); box-shadow:0 0 14px rgba(0,217,255,0.55); }
.ceo .aria-help__body { flex:1; min-width:0; }
.ceo .aria-help__title { font-size:14.5px; font-weight:800; color:var(--text); }
.ceo .aria-help__text { margin-top:4px; font-size:13px; line-height:1.5; color:var(--text-2); }
.ceo .aria-help__x { flex:none; width:26px; height:26px; border:none; border-radius:8px; background:rgba(255,255,255,0.06); color:var(--text-2); font-size:16px; line-height:1; cursor:pointer; }
.ceo .aria-help__x:hover { background:rgba(255,255,255,0.14); color:#fff; }
.ceo.theme-light .aria-help { background:rgba(0,150,200,0.07); border-color:rgba(0,150,200,0.25); }
.ceo.theme-light .aria-help__text { color:#5c5c63; }
.ceo.theme-light .aria-help__x { background:rgba(0,0,0,0.05); color:#5c5c63; }

/* ── Number picker (search + buy a Twilio number) ───────────────────────── */
.ceo .numpick-search { display:flex; gap:8px; margin-top:16px; }
.ceo .numpick-input { flex:1; min-width:0; font:inherit; font-size:14px; padding:11px 13px; border-radius:11px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.14); color:var(--text); }
.ceo .numpick-input::placeholder { color:var(--text-3); }
.ceo .numpick-input:focus { outline:none; border-color:rgba(0,217,255,0.5); }
.ceo .numpick-go { flex:none; font:inherit; font-size:13.5px; font-weight:700; color:#fff; background:linear-gradient(160deg, #0a84ff 0%, #0a5fd0 100%); border:none; border-radius:11px; padding:0 18px; cursor:pointer; }
.ceo .numpick-go:hover { filter:brightness(1.06); }
.ceo .numpick-list { margin-top:14px; display:flex; flex-direction:column; gap:8px; max-height:46vh; overflow-y:auto; }
.ceo .numpick-row { display:flex; align-items:center; gap:12px; padding:11px 13px; border-radius:12px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); }
.ceo .numpick-main { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
.ceo .numpick-num { font-size:15px; font-weight:700; color:var(--text); letter-spacing:0.01em; }
.ceo .numpick-loc { font-size:12px; color:var(--text-2); }
.ceo .numpick-buy { flex:none; font:inherit; font-size:13px; font-weight:700; color:#0a63ff; background:#fff; border:none; border-radius:999px; padding:8px 16px; cursor:pointer; transition:filter .15s; }
.ceo .numpick-buy:hover:not(:disabled) { filter:brightness(0.94); }
.ceo .numpick-buy:disabled { opacity:0.5; cursor:default; }
.ceo.theme-light .numpick-input { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
.ceo.theme-light .numpick-row { background:#f5f6f9; border-color:rgba(0,0,0,0.06); }
.ceo.theme-light .numpick-buy { background:#0a63ff; color:#fff; }

/* ── Vision board v1 — "Why you're doing this" ──────────────────────────── */
.ceo .vision { max-width:920px; margin:30px auto 0; padding:24px 28px; border-radius:22px; display:flex; align-items:center; gap:26px; background:#141a24; border:1px solid rgba(255,255,255,0.14); backdrop-filter:blur(40px) saturate(180%); -webkit-backdrop-filter:blur(40px) saturate(180%); box-shadow:inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 24px rgba(0,0,0,0.4); }
.ceo .vision__main { flex:1; min-width:0; }
.ceo .vision__kicker { font-size:11px; font-weight:700; letter-spacing:0.22em; text-transform:uppercase; color:var(--brandA, #91ba38); margin-bottom:8px; }
.ceo .vision__goal { font-size:clamp(22px, 3vw, 34px); font-weight:800; letter-spacing:-0.02em; line-height:1.15; color:var(--text); }
.ceo .vision__bar { margin-top:16px; }
.ceo .vision__barTrack { height:10px; border-radius:999px; background:rgba(255,255,255,0.10); overflow:hidden; }
.ceo .vision__barFill { height:100%; border-radius:999px; background:linear-gradient(90deg, var(--brandA, #91ba38), #d4ef9a); box-shadow:0 0 12px rgba(145,186,56,.5); transition:width .6s cubic-bezier(0.2,0.8,0.2,1); }
.ceo .vision__barLabel { margin-top:7px; font-size:12px; color:var(--text-2); font-variant-numeric:tabular-nums; }
.ceo .vision__form { display:flex; gap:10px; margin-top:4px; }
.ceo .vision__input { flex:1; min-width:0; font:inherit; font-size:14px; padding:12px 14px; border-radius:12px; background:rgba(255,255,255,0.05); border:1.5px solid rgba(255,255,255,0.14); color:var(--text); }
.ceo .vision__input::placeholder { color:var(--text-3); }
.ceo .vision__input:focus { outline:none; border-color:var(--brandA, #91ba38); }
.ceo .vision__save { flex:none; font:inherit; font-size:13.5px; font-weight:700; color:#fff; background:var(--brandA, #6e9527); border:none; border-radius:12px; padding:0 20px; cursor:pointer; }
.ceo .vision__save:hover { filter:brightness(1.08); }
.ceo .vision__edit { margin-top:10px; font:inherit; font-size:12px; color:var(--text-3); background:transparent; border:none; padding:0; cursor:pointer; }
.ceo .vision__edit:hover { color:var(--text-2); text-decoration:underline; }
/* Pinned mini-polaroids next to the goal (higher specificity than the fixed
   photo-wall rules, so these stay in-flow and visible at every width). */
.ceo .vision__photos { flex:none; display:flex; gap:14px; }
.ceo .vision__photos .ceo-polaroid { display:block; position:relative; width:128px; padding:6px 6px 20px; left:auto; right:auto; top:auto; }
.ceo .vision__photos .ceo-polaroid img { height:92px; }
/* mini pins keep small tape — the big wall-tape size would swallow a 128px photo */
.ceo .vision__photos .ceo-polaroid .tp { width:40px; height:14px; }
.ceo .vision__photos .vp0 { transform:rotate(-4deg); }
.ceo .vision__photos .vp1 { transform:rotate(3deg); }
.ceo.theme-light .vision { background:#fff; border:1px solid rgba(0,0,0,0.10); box-shadow:0 6px 16px rgba(0,0,0,0.10); }
.ceo.theme-light .vision__barTrack { background:rgba(0,0,0,0.08); }
.ceo.theme-light .vision__input { background:#f5f6f9; border-color:rgba(0,0,0,0.12); color:#1d1d1f; }
@media (max-width:760px){ .ceo .vision { flex-direction:column; align-items:stretch; } .ceo .vision__photos { justify-content:center; } }
`;

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3a9 9 0 1 0 9 9c0-.34-.02-.68-.06-1.01a5.5 5.5 0 0 1-7.93-7.93C12.68 3.02 12.34 3 12 3z" />
  </svg>
);
const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.4 1.4M17.6 17.6L19 19M5 19l1.4-1.4M17.6 6.4L19 5" />
  </svg>
);

function Bubble({ label, value, tone, sign, onClick }) {
  const cls = (tone === 'good' || tone === 'bad' || tone === 'warn') ? tone : 'neutral';
  // Sign is now EXPLICIT, not derived from tone. The owner's only positive is Net,
  // so only Net carries '+'; Expenses (money out) carries '−'. Revenue and Gross
  // are just totals — no sign.
  const isMoney = String(value).includes('$');
  const display = isMoney && sign ? sign + value : value;
  return (
    <div
      className={`bub bub--${tone || "plain"} ${cls}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="bub-label">{label}</div>
      <div className="bub-value">{display}</div>
    </div>
  );
}

// The "click a number → see exactly what's behind it" panel. Lists every line
// item with a date and a Done/Drafted tag, scoped to the selected period.
function DrillDown({ metric, fin, periodLabel, onClose }) {
  const items = (fin && fin.ledgers && fin.ledgers[metric]) || [];
  const total = metric === 'net' ? (fin && fin.summary ? fin.summary.net : 0) : items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const totalSign = metric === 'expenses' ? '−' : metric === 'net' ? '+' : '';
  return (
    <div className="ceo-drill" onClick={onClose}>
      <div className="ceo-drill__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="ceo-drill__close" onClick={onClose} aria-label="Close">×</button>
        <div className="ceo-drill__title">{METRIC_TITLE[metric] || metric}</div>
        <div className="ceo-drill__sub">{metric === 'unpaid' ? 'outstanding right now' : periodLabel} · {items.length} {items.length === 1 ? 'item' : 'items'}</div>
        <div className="ceo-drill__total">{totalSign}{fmtMoney(total)}</div>
        <div className="ceo-drill__list">
          {items.length === 0 && <div className="ceo-drill__empty">Nothing here yet for {periodLabel}.</div>}
          {items.map((it, idx) => (
            <div className="ceo-drill__row" key={idx}>
              <div className="ceo-drill__rmain">
                <div className="ceo-drill__rtitle">{it.title}{it.customer ? ` · ${it.customer}` : ''}</div>
                <div className="ceo-drill__rmeta">
                  {it.when && <span className={`ceo-drill__tag ceo-drill__tag--${it.when}`}>{it.when === 'completed' ? 'Done' : it.when === 'drafted' ? 'Drafted' : it.when === 'invoiced' ? 'Invoiced' : it.when}</span>}
                  {it.at && <span className="ceo-drill__date">{fmtDate(it.at)}</span>}
                  {it.status && <span className="ceo-drill__status">{it.status}</span>}
                </div>
              </div>
              <div className="ceo-drill__amt">{it.sign === '-' ? '−' : ''}{fmtMoney(it.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// One agent box on the board. Number gone, title only. Not set up → "See more" +
// "Set up". Set up → blue/active, whole card opens the detail.
// Rows that have a REAL page in the app go straight to it; everything else
// opens the agent panel. Verified against App.jsx's section list 8/10 — 'leads'
// and 'review' are the only agent-shaped sections that actually exist. Jobs and
// invoicing live inside the company page itself (the "+ New Job" button), not
// as their own routes, so they are deliberately absent here rather than guessed.
const AGENT_SECTION = {
  leads: 'leads',
  reviews: 'review',
};

// Braeden 8/10: the seven rows, exactly as he mocked them up — heavy label,
// muted detail, blue "Open X ->" on the right. His mock showed five (Website,
// Google, Customers, Money, Books); Leads and Jobs are the two he named out
// loud ("including leads, including jobs"). `section` is only set where a real
// route exists in App.jsx — the rest open the agent panel.
const BIG_ROWS = [
  // detail is overridden at render with the owner's real domain when the company
  // record has one; the deck's placeholder only shows if nothing is set.
  { key: 'website',   label: 'Website',   detail: 'simmonsplumbing.com',                 action: 'Open site',     agent: null },
  { key: 'google',    label: 'Google',    detail: '4.8 ★ · 127 reviews',       action: 'Open listing',  agent: 'reviews', section: 'google' },
  { key: 'leads',     label: 'Leads',     detail: 'answered in 60 seconds, day or night', action: 'Open leads',   agent: 'leads',   section: 'leads' },
  // Customers row removed 8/10 (owner).
  { key: 'jobs',      label: 'Jobs',      detail: 'book a job, then track every one',    action: 'Open jobs',     agent: 'scheduling', section: 'jobs' },
  // Money folded into Books (Braeden 8/12): "get rid of the money one and just
  // put it in the books one, thats where it should be anyway." He is right —
  // invoicing IS bookkeeping. Two rows made the owner check two places for one
  // question ("did I get paid?"), and split the money story across the board.
  // Same call he made on the pitch deck 8/11, so the site now matches it.
  // TWO rows, not one (Braeden 8/13: "there should be two tabs, one says book,
  // one says system, you got rid of the books one"). Books is the money view
  // he already had; Systems is the full record of everything.
  // 8/13, second pass: the two were backwards. Books was listing the 27
  // automations (a description of the machinery) while Systems opened the
  // record in a modal. "the systems needs to have everything the books ones
  // has in it, than make a new layout for books, ONLY records." So Systems is
  // the machinery page and Books is the ledger — both real pages now.
  { key: 'books',     label: 'Books',     detail: 'the record — every job, dollar and move she made', action: 'Open books', agent: null, section: 'books' },
  { key: 'system',    label: 'Systems',   detail: 'every automation she runs, and whether it is on', action: 'Open systems', agent: null, section: 'system' },
];

function AgentCard({ agent, setup, onOpen, showSetup = true }) {
  if (setup) {
    return (
      <button type="button" className="agent2 agent2--on" onClick={() => onOpen(agent.key)}>
        <span className="agent2__title">{agent.title}</span>
        <span className="agent2__detail">{agent.cadence}</span>
        <span className="agent2__active"><span className="agent2__dot" />Active</span>
      </button>
    );
  }
  // Braeden 8/10: the row is label | detail | action, matching the deck-slide
  // pattern he sent. The old markup nested the title and "See more" together in
  // a titlewrap, which can't sit in that three-column grid — so the actions are
  // grouped on the right instead and the middle column carries the cadence.
  return (
    <div className="agent2 agent2--off">
      <span className="agent2__title">{agent.title}</span>
      <span className="agent2__detail">{agent.cadence}</span>
      <span className="agent2__actions">
        <button type="button" className="agent2__morelink" onClick={() => onOpen(agent.key)}>See more</button>
        {showSetup && <button type="button" className="agent2__setup" onClick={() => onOpen(agent.key)}>Set up</button>}
      </span>
    </div>
  );
}

// The per-agent panel: what it does + what's inside + Set up (or Active). The
// agent-specific controls get filled in one by one; this is the shared shell.
function AgentDetail({ agent, setup, onClose, onActivate }) {
  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">{agent.title}</div>
        <div className="agent-detail__cadence">{agent.cadence}</div>
        <p className="agent-detail__desc">{agent.desc}</p>
        <div className="agent-detail__section">What it does</div>
        <ul className="agent-detail__list">
          {agent.includes.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
        {setup ? (
          <div className="agent-detail__status">✓ Active — running for you</div>
        ) : (
          <button type="button" className="agent-detail__cta" onClick={() => onActivate(agent.key)}>Set up {agent.title}</button>
        )}
        <div className="agent-detail__note">The controls for this agent are being wired in next — this is the framework.</div>
      </div>
    </div>
  );
}

// The owner's middleman inbox — everything Aria scanned and flagged as theirs.
const NOTIF_ICON = { lead: '🎯', message: '💬', payment: '💳', review: '⭐', calendar: '📅', job: '🧰', system: '•' };
// BooksPanel was removed 8/13 - the record it rendered is now the Books
// page (BooksView.jsx), which owns the ledger layout.


function NotificationsPanel({ ownerId, onClose, onUnreadChange }) {
  const base = getApiBase();
  const [items, setItems] = React.useState(null);
  const load = React.useCallback(async () => {
    try {
      const d = await fetch(`${base}/api/notifications?ownerId=${encodeURIComponent(ownerId)}`).then((r) => r.json());
      const list = d && d.ok ? d.notifications : [];
      setItems(list);
      if (onUnreadChange) onUnreadChange(list.filter((x) => x.status === 'unread').length); // keep the bell badge in sync with the server
    } catch (_) { setItems([]); }
  }, [base, ownerId, onUnreadChange]);
  React.useEffect(() => { load(); }, [load]);
  const readOne = async (n) => {
    if (n.status !== 'unread') return;
    setItems((its) => its.map((x) => (x.id === n.id ? { ...x, status: 'read' } : x)));
    if (onUnreadChange) onUnreadChange((c) => Math.max(0, (typeof c === 'number' ? c : 0) - 1));
    try { await fetch(`${base}/api/notifications/${n.id}/read`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ownerId }) }); } catch (_) {}
  };
  const readAll = async () => {
    setItems((its) => its.map((x) => ({ ...x, status: 'read' })));
    if (onUnreadChange) onUnreadChange(() => 0);
    try { await fetch(`${base}/api/notifications/read-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ownerId }) }); } catch (_) {}
  };
  const fmt = (at) => { if (!at) return ''; try { return new Date(String(at).replace(' ', 'T') + 'Z').toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return at; } };
  const unreadN = (items || []).filter((x) => x.status === 'unread').length;
  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">What’s come in</div>
        <div className="agent-detail__cadence">Aria flags anything that’s yours</div>
        <div className="notif-list">
          {items == null ? <div className="sched-muted">Loading…</div>
            : items.length === 0 ? <div className="sched-muted">Nothing yet — new leads, customer messages, payments and reviews will show up here as they come in.</div>
            : items.map((n) => (
              <button type="button" key={n.id} className={`notif-row${n.status === 'unread' ? ' notif-row--unread' : ''}`} onClick={() => readOne(n)}>
                <span className="notif-ic">{NOTIF_ICON[n.kind] || '•'}</span>
                <span className="notif-main">
                  <span className="notif-title">{n.title}{n.urgency === 'high' && <span className="notif-urgent">urgent</span>}</span>
                  {n.body && <span className="notif-body">{n.body}</span>}
                  <span className="notif-meta">{n.source ? n.source + ' · ' : ''}{fmt(n.created_at)}</span>
                </span>
                {n.status === 'unread' && <span className="notif-dot" />}
              </button>
            ))}
        </div>
        {unreadN > 0 && <button type="button" className="notif-readall" onClick={readAll}>Mark all read</button>}
      </div>
    </div>
  );
}

// +14155551234 → (415) 555-1234 for display.
function formatPhone(e164) {
  const d = String(e164 || '').replace(/\D/g, '');
  const ten = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return e164 || '';
}

// The "Get a text number" popup. Search by area code or city, see the monthly
// price, and claim a local number for the owner's reminders. Buying spends real
// money (recurring), so it always confirms first and posts confirm:true.
function NumberPicker({ ownerId, pricing, onClose, onBought }) {
  const base = getApiBase();
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [busyNum, setBusyNum] = React.useState('');
  const [err, setErr] = React.useState('');

  const search = React.useCallback(async (term) => {
    setLoading(true); setErr('');
    try {
      const d = await fetch(`${base}/api/phone/search?q=${encodeURIComponent(term || '')}&limit=6`).then((r) => r.json());
      if (!d.ok) {
        setErr(d.error === 'TWILIO_NOT_CONFIGURED' ? 'Texting isn’t connected on the server yet.' : 'Couldn’t load numbers — try again.');
        setResults([]);
      } else setResults(d.numbers || []);
    } catch (_) { setErr('Couldn’t reach the server.'); setResults([]); }
    setLoading(false);
  }, [base]);

  React.useEffect(() => { search(''); }, [search]); // show example numbers on open

  const buy = async (num) => {
    if (!window.confirm(`Get ${formatPhone(num.number)} for $${num.monthlyPriceUsd}/mo? This starts a monthly charge.`)) return;
    setBusyNum(num.number); setErr('');
    try {
      const d = await fetch(`${base}/api/phone/buy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, number: num.number, confirm: true }),
      }).then((r) => r.json());
      if (d.ok) { onBought(d.number); return; }
      setErr(d.message || d.error || 'Couldn’t get that number.');
    } catch (_) { setErr('Purchase failed — try again.'); }
    setBusyNum('');
  };

  const mo = pricing && pricing.monthly ? `$${pricing.monthly.priceUsd}/mo` : '';
  const perText = pricing && pricing.perText ? `~${Math.round(pricing.perText.priceUsd * 100)}¢ per text` : '';

  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">Get a text number</div>
        <p className="agent-detail__desc" style={{ marginTop: '10px' }}>
          Reminders go out from a local number you own, so customers recognize it.
          {mo && <> {mo}{perText ? ` · ${perText}` : ''}.</>}
        </p>
        <form className="numpick-search" onSubmit={(e) => { e.preventDefault(); search(q); }}>
          <input
            className="numpick-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="City or area code"
            inputMode="text"
            autoFocus
          />
          <button type="submit" className="numpick-go">Search</button>
        </form>
        {err && <div className="sched-warn" style={{ marginTop: '12px' }}>{err}</div>}
        <div className="numpick-list">
          {loading ? <div className="sched-muted">Finding numbers…</div>
            : !results ? null
            : results.length === 0 ? <div className="sched-muted">No numbers for that — try an area code like 415 or a different city.</div>
            : results.map((n) => (
              <div className="numpick-row" key={n.number}>
                <span className="numpick-main">
                  <span className="numpick-num">{formatPhone(n.number)}</span>
                  <span className="numpick-loc">{[n.city, n.region].filter(Boolean).join(', ') || 'US'} · ${n.monthlyPriceUsd}/mo</span>
                </span>
                <button
                  type="button"
                  className="numpick-buy"
                  disabled={!!busyNum}
                  onClick={() => buy(n)}
                >{busyNum === n.number ? 'Getting…' : 'Get this'}</button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// Scheduling's own panel — wired to the real Google Calendar OAuth that already
// exists in the backend (routes/calendar.js + calendarAgent). Connect → see your
// live upcoming events in-app → set job text reminders.
function SchedulingDetail({ ownerId, onClose, onConnectedChange }) {
  const base = getApiBase();
  const [st, setSt] = React.useState({ loading: true });
  const [events, setEvents] = React.useState(null);
  const [rem, setRem] = React.useState(() => { try { return JSON.parse(localStorage.getItem('howto_sched_reminders') || '{"on":true,"lead":"1d"}'); } catch (_) { return { on: true, lead: '1d' }; } });
  const [picker, setPicker] = React.useState(false);   // number-picker popup open
  const [pricing, setPricing] = React.useState(null);   // /api/phone/pricing
  const [myNumber, setMyNumber] = React.useState(null); // owner's current sending number
  const [helpOpen, setHelpOpen] = React.useState(true); // Aria "Want some help?" intro

  React.useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`${base}/api/phone/pricing`).then((r) => r.json()).catch(() => null),
      fetch(`${base}/api/phone/owned?ownerId=${encodeURIComponent(ownerId)}`).then((r) => r.json()).catch(() => null),
    ]).then(([p, o]) => { if (!alive) return; if (p && p.ok) setPricing(p); if (o && o.ok) setMyNumber(o.number || null); });
    return () => { alive = false; };
  }, [base, ownerId]);

  const refresh = React.useCallback(async () => {
    try {
      const [h, c] = await Promise.all([
        fetch(`${base}/api/calendar/health`).then((r) => r.json()).catch(() => ({ ok: false })),
        fetch(`${base}/api/calendar/connected?userId=${encodeURIComponent(ownerId)}`).then((r) => r.json()).catch(() => ({ connected: false })),
      ]);
      const connected = !!c.connected;
      setSt({ loading: false, configured: !!h.ok, connected });
      if (onConnectedChange) onConnectedChange(connected);
      if (connected) {
        const u = await fetch(`${base}/api/calendar/upcoming?userId=${encodeURIComponent(ownerId)}&max=8`).then((r) => r.json()).catch(() => ({ ok: false }));
        setEvents(u && u.ok ? u.events : []);
      }
    } catch (_) { setSt({ loading: false, configured: false, connected: false }); }
  }, [base, ownerId, onConnectedChange]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const connect = () => { try { window.open(`${base}/api/calendar/oauth/url?userId=${encodeURIComponent(ownerId)}`, 'aria_gcal', 'width=520,height=700'); } catch (_) {} };
  const setReminders = (next) => { setRem(next); try { localStorage.setItem('howto_sched_reminders', JSON.stringify(next)); } catch (_) {} };
  const fmtEv = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return iso; } };

  return (
    <div className="agent-page">
      <div className="agent-page__inner">
        <button type="button" className="agent-page__back" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Back
        </button>
        <div className="agent-page__title">Scheduling</div>
        <div className="agent-page__sub">Your live Google Calendar, plus automatic job reminders.</div>

        <SetupHelpBox
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          agentTitle="Scheduling"
          ownerId={ownerId}
          greeting={st.connected
            ? "Nice — your calendar’s connected. Next, let’s get you a text number to remind customers."
            : "Need some help? First, let’s connect your Google account so I can see your calendar."}
          actions={st.connected
            ? [{ label: 'Get a text number', onClick: () => { setHelpOpen(false); setPicker(true); } }]
            : [{ label: 'Connect Google Calendar', onClick: connect }]}
        />

        <div className="agent-page__section">Step 1 · Your calendar</div>
        <div className="agent-page__card" style={{ minHeight: 'calc(100vh - 230px)' }}>
        {st.loading ? (
          <div className="sched-muted">Checking your calendar…</div>
        ) : !st.configured ? (
          <div className="sched-warn">Connecting Google Calendar needs the app’s Google keys on the server first (GOOGLE_CALENDAR_CLIENT_ID + SECRET). Once those are set, you can connect in one tap right here.</div>
        ) : !st.connected ? (
          <>
            <div className="sched-muted">Not connected yet — link your Google account so Aria can see your calendar and book jobs on it.</div>
            <button type="button" className="agent-detail__cta" onClick={connect}>Connect Google Calendar</button>
            <button type="button" className="sched-refresh" onClick={refresh}>I’ve connected — refresh</button>
          </>
        ) : (
          <>
            <div className="sched-ok">✓ Connected — Aria can read your calendar and book jobs on it.</div>
            <div className="sched-events">
              {events == null ? <div className="sched-muted">Loading events…</div>
                : events.length === 0 ? <div className="sched-muted">No upcoming events.</div>
                : events.map((e) => (
                  <div className="sched-event" key={e.id}>
                    <div className="sched-event__t">{e.title}</div>
                    <div className="sched-event__m">{fmtEv(e.start)}{e.location ? ` · ${e.location}` : ''}</div>
                  </div>
                ))}
            </div>
            <a className="sched-link" href="https://calendar.google.com/calendar/u/0/r" target="_blank" rel="noreferrer">Open Google Calendar ↗</a>
          </>
        )}
        </div>

        <div className="agent-page__section">Step 2 · Text reminders</div>
        <div className="agent-page__card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Your text number</span>
            <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-2)', marginTop: '3px' }}>
              {myNumber
                ? <>Reminders send from <strong>{formatPhone(myNumber)}</strong>.</>
                : <>Get a local number so reminders come from you.{pricing && pricing.monthly && <> {`$${pricing.monthly.priceUsd}/mo · ~${Math.round(pricing.perText.priceUsd * 100)}¢ per text.`}</>}</>}
            </span>
          </span>
          <button type="button" className="numpick-buy" onClick={() => setPicker(true)}>{myNumber ? 'Change' : 'Get a number'}</button>
        </div>

        <div className="agent-page__card" style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: '12px' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Auto-remind customers</span>
            <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-2)', marginTop: '3px' }}>Texts the customer a reminder before each scheduled job.</span>
          </span>
          <label className="aswitch">
            <input type="checkbox" checked={!!rem.on} onChange={(e) => setReminders({ ...rem, on: e.target.checked })} />
            <span className="aswitch__track" />
            <span className="aswitch__knob" />
          </label>
        </div>
        {rem.on && (
          <div className="sched-leadrow" style={{ marginTop: '12px' }}>
            <span className="sched-leadlabel">Send the reminder</span>
            <select className="sched-select" value={rem.lead} onChange={(e) => setReminders({ ...rem, lead: e.target.value })}>
              <option value="2h">2 hours before</option>
              <option value="1d">1 day before</option>
              <option value="2d">2 days before</option>
            </select>
          </div>
        )}
        <div className="agent-detail__note">{myNumber ? 'Reminders are ready to send from your number.' : 'Grab a number above and reminders will start going out by text.'}</div>

        {picker && (
          <NumberPicker
            ownerId={ownerId}
            pricing={pricing}
            onClose={() => setPicker(false)}
            onBought={(num) => { setMyNumber(num); setPicker(false); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Vision board v1 — "Why you're doing this" ───────────────────────────────
// The owner's goal, big, next to a couple of their real brand photos (small
// pinned polaroids), plus a progress bar of this month's revenue against the
// goal's $ amount when the goal contains one (e.g. "$10k months").
function VisionBoard({ ownerId, brand }) {
  const [goal, setGoal] = React.useState(() => { try { return localStorage.getItem('aria_owner_goal') || ''; } catch (_) { return ''; } });
  const [draft, setDraft] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [revenue, setRevenue] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetch(`${getApiBase()}/api/drawers/dashboard?ownerId=${encodeURIComponent(ownerId)}&business=${encodeURIComponent('junk removal')}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d || !d.ok) return;
        const tile = (d.tiles || []).find((t) => t.metric === 'revenueMonth');
        if (tile && typeof tile.value === 'number') setRevenue(tile.value);
      }).catch(() => {});
    return () => { alive = false; };
  }, [ownerId]);
  const save = () => {
    const g = draft.trim();
    if (!g) return;
    setGoal(g); setEditing(false);
    try { localStorage.setItem('aria_owner_goal', g); } catch (_) {}
    // No /api/company/goal route on the backend yet — localStorage only for v1.
  };
  // "$3k" / "$10,000" / "$1.5m" in the goal → the target the bar fills toward.
  const target = React.useMemo(() => {
    const m = String(goal).replace(/,/g, '').match(/\$\s*(\d+(?:\.\d+)?)\s*([km])?/i);
    if (!m) return null;
    const mult = m[2] ? (m[2].toLowerCase() === 'm' ? 1e6 : 1e3) : 1;
    const n = Number(m[1]) * mult;
    return n > 0 ? n : null;
  }, [goal]);
  const pct = (target && revenue != null) ? Math.max(0, Math.min(100, Math.round((revenue / target) * 100))) : null;
  const photos = ((brand && brand.photos) || []).slice(0, 2);
  return (
    <div className="vision">
      <div className="vision__main">
        <div className="vision__kicker">Why you're doing this</div>
        {(!goal || editing) ? (
          <form className="vision__form" onSubmit={(e) => { e.preventDefault(); save(); }}>
            <input className="vision__input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="What are you working toward?" />
            <button type="submit" className="vision__save">Save</button>
          </form>
        ) : (
          <>
            <div className="vision__goal">{goal}</div>
            {pct != null && (
              <div className="vision__bar">
                <div className="vision__barTrack"><div className="vision__barFill" style={{ width: pct + '%' }} /></div>
                <div className="vision__barLabel">{fmtMoney(revenue)} of {fmtMoney(target)} this month · {pct}%</div>
              </div>
            )}
            <button type="button" className="vision__edit" onClick={() => { setDraft(goal); setEditing(true); }}>Edit goal</button>
          </>
        )}
      </div>
      {photos.length > 0 && (
        <div className="vision__photos" aria-hidden>
          {photos.map((u, i) => <span key={i} className={`ceo-polaroid vp${i}`}><img src={u} alt="" /></span>)}
        </div>
      )}
    </div>
  );
}

export default function CompanyView({ project, businessName, onNavigate, theme = 'night' }) {
  // `theme` ('night' | 'day') comes from the global app theme (header switch).
  const [period, setPeriod] = React.useState('month'); // 'week' | 'month' | 'year'
  // 24/7 agent on/off + where it runs + the owner config from Aria's setup convo.
  const [agentActive, setAgentActive] = React.useState(() => { try { return localStorage.getItem('howto_agent_active') === '1'; } catch (_) { return false; } });
  const [runLocation, setRunLocation] = React.useState(() => { try { return localStorage.getItem('howto_agent_runloc') || 'pc'; } catch (_) { return 'pc'; } });
  const [agentConfig, setAgentConfig] = React.useState(() => { try { return JSON.parse(localStorage.getItem('howto_agent_config') || '{}'); } catch (_) { return {}; } });
  // Setup wizard (Aria's onboarding conversation).
  const [setupOpen, setSetupOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [bizType, setBizType] = React.useState(() => (agentConfig.businessType) || '');
  const [bizName, setBizName] = React.useState(() => (agentConfig.businessName) || (project && project.name) || '');
  const [picked, setPicked] = React.useState(() => (agentConfig.enabledServices) || []);
  const seededBizRef = React.useRef(agentConfig.businessType || '');

  // ── Real money (owner-scoped, period-filtered) + the drill-down panel ──────
  const ownerId = React.useMemo(() => {
    try { return (localStorage.getItem('aria_user_id') || localStorage.getItem('howto_owner_id') || 'owner-local').trim(); } catch (_) { return 'owner-local'; }
  }, []);
  const [fin, setFin] = React.useState(null);
  // ONBOARDING (8/13): what she still has to be told, per surface. Keyed by
  // row key so a row can show "3 questions" instead of pretending it works.
  const [onboard, setOnboard] = React.useState({});
  const [onboardKey, setOnboardKey] = React.useState(null);
  const loadOnboarding = React.useCallback(() => {
    const bookOwner = (() => { try { return localStorage.getItem('howto_owner_id') || 'owner-default'; } catch (_) { return 'owner-default'; } })();
    let tok = ''; try { tok = localStorage.getItem('aria_session_token') || ''; } catch (_) {}
    fetch(`${getApiBase()}/api/onboarding?ownerId=${encodeURIComponent(bookOwner)}`,
      tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined)
      .then((r) => r.json())
      .then((d) => {
        if (!d || !d.ok) return;
        const m = {};
        for (const s of d.surfaces || []) m[s.key] = s;
        setOnboard(m);
      })
      .catch(() => {});
  }, []);
  React.useEffect(() => { loadOnboarding(); }, [loadOnboarding]);
  
  // Custom scroll indicator: native bar is hidden (edge-to-edge banner), this
  // rounded thumb fades in while scrolling and melts away when idle.
  React.useEffect(() => {
    const sc = document.querySelector('.aria-scroll.full-bleed');
    if (!sc) return undefined;
    const bar = document.createElement('div');
    bar.style.cssText = 'position:fixed;right:5px;width:7px;border-radius:99px;background:rgba(255,255,255,.72);opacity:0;transition:opacity .35s;z-index:900;pointer-events:none;box-shadow:0 0 6px rgba(0,0,0,.35)';
    document.body.appendChild(bar);
    let t = null;
    const onScroll = () => {
      const frac = sc.clientHeight / sc.scrollHeight;
      const h = Math.max(44, frac * sc.clientHeight * 0.94);
      const top = 58 + (sc.scrollTop / (sc.scrollHeight - sc.clientHeight)) * (sc.clientHeight * 0.94 - h);
      bar.style.height = h + 'px'; bar.style.top = top + 'px'; bar.style.opacity = '1';
      if (t) clearTimeout(t);
      t = setTimeout(() => { bar.style.opacity = '0'; }, 700);
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => { sc.removeEventListener('scroll', onScroll); if (t) clearTimeout(t); bar.remove(); };
  }, []);
  const [drill, setDrill] = React.useState(null); // metric key for the open list
  // Owner's real brand (logo + site photos) — tailors the profile to THEM.
  const [brand, setBrand] = React.useState(null);
  React.useEffect(() => {
    let ok = true;
    let timer = null;
    // retry with backoff — the backend restarts often in dev, and a single
    // failed fetch here used to blank the whole banner for the session
    const tryLoad = (attempt) => {
      fetch(`${getApiBase()}/api/company/brand?ownerId=${encodeURIComponent(ownerId)}`)
        .then((r) => r.json())
        .then((d) => {
          if (!ok) return;
          if (d && d.ok && (d.photos || []).length) {
            setBrand(d);
            try { if (d.ownerEmail && !localStorage.getItem('aria_session_email')) localStorage.setItem('aria_session_email', d.ownerEmail); } catch (_) {}
          } else if (attempt < 5) timer = setTimeout(() => tryLoad(attempt + 1), 2500 * (attempt + 1));
        })
        .catch(() => { if (ok && attempt < 5) timer = setTimeout(() => tryLoad(attempt + 1), 2500 * (attempt + 1)); });
    };
    tryLoad(0);
    return () => { ok = false; if (timer) clearTimeout(timer); };
  }, [ownerId]);
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
  // Aria-generated layout config (brandLayoutGenerator) — when present its
  // values are applied over the hand-built defaults; absent → fallback look.
  const [layout, setLayout] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetch(`${getApiBase()}/api/company/layout?ownerId=${encodeURIComponent(ownerId)}`)
      .then((r) => r.json())
      .then((d) => { if (alive && d && d.ok && d.layout) setLayout(d.layout); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ownerId]);
  React.useEffect(() => {
    const el = document.querySelector('.ceo');
    if (!el) return;
    if (brand) {
      if (brand.accent) el.style.setProperty('--brandA', brand.accent);
      if (brand.color) el.style.setProperty('--brandB', brand.color);
    }
    if (layout) {
      // Inject the generated recipe as CSS variables — the stylesheet keeps
      // the hand-built Open Corp values as var() fallbacks.
      const set = (k, v) => { if (v != null && v !== '') el.style.setProperty(k, String(v)); };
      const p = layout.palette || {};
      set('--brandA', p.accent);
      set('--brandB', p.accentDark || p.themeColor);
      const t = layout.titleRecipe || {};
      set('--titleG1', t.g1); set('--titleG2', t.g2); set('--titleG3', t.g3);
      set('--titleStroke', t.stroke);
      if (t.strokeWidthPx) { set('--titleStrokeW', t.strokeWidthPx + 'px'); set('--titleStrokeWL', Math.max(1, t.strokeWidthPx - 0.4) + 'px'); }
      const w = layout.wall || {};
      if (w.maxWPx) set('--wallW', `clamp(${w.minWPx || 230}px, ${Math.round((w.maxHVh || 44) * 34 / 44)}vh, ${w.maxWPx}px)`);
      if (w.maxHVh) set('--wallH', w.maxHVh + 'vh');
      if (layout.banner && layout.banner.mode === 'gradient' && layout.banner.css) set('--bannerGrad', layout.banner.css);
    }
  }, [brand, layout]);
  // Live values for the generated tile row (archetype tiles from the layout).
  const [genTiles, setGenTiles] = React.useState(null);
  React.useEffect(() => {
    if (!layout || !Array.isArray(layout.tiles) || !layout.tiles.length) { setGenTiles(null); return undefined; }
    let alive = true;
    fetch(`${getApiBase()}/api/drawers/dashboard?ownerId=${encodeURIComponent(ownerId)}&business=${encodeURIComponent(layout.businessText || '')}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setGenTiles(d && d.ok && Array.isArray(d.tiles) && d.tiles.length ? d.tiles : layout.tiles); })
      .catch(() => { if (alive) setGenTiles(layout.tiles); });
    return () => { alive = false; };
  }, [layout, ownerId]);
  const [portalMode, setPortalMode] = React.useState('cal'); // cal | map

  // Owner location (opt-in) — used to center the job map on where you are so Aria
  // can route jobs / show what's nearby. Requested only when the owner taps the
  // button; the browser/OS shows the permission prompt. Stored locally only.
  const [myLoc, setMyLoc] = React.useState(() => { try { const s = localStorage.getItem('aria_owner_loc'); return s ? JSON.parse(s) : null; } catch (_) { return null; } });
  const [locStatus, setLocStatus] = React.useState(''); // '' | 'asking' | 'denied' | 'error'
  const requestLocation = () => {
    setLocStatus('asking');
    const useLoc = (loc) => {
      setMyLoc(loc); setLocStatus('');
      try { localStorage.setItem('aria_owner_loc', JSON.stringify(loc)); } catch (_) {}
    };
    // Precise (device GPS/OS) can't always resolve in the desktop shell, so fall
    // back to an approximate IP-based location (free, no key) so the map still
    // centers on the owner's area.
    const ipFallback = async () => {
      for (const url of ['https://ipwho.is/', 'https://ipapi.co/json/']) {
        try {
          const r = await fetch(url); const d = await r.json();
          const lat = +(d.latitude); const lng = +(d.longitude);
          if (isFinite(lat) && isFinite(lng) && (lat || lng)) { useLoc({ lat: +lat.toFixed(6), lng: +lng.toFixed(6), approx: true }); return; }
        } catch (_) {}
      }
      setLocStatus('error');
    };
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => useLoc({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }),
        (err) => { if (err && err.code === 1) setLocStatus('denied'); else ipFallback(); },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 120000 }
      );
    } else { ipFallback(); }
  };
  const [calChoice, setCalChoice] = React.useState(() => { try { return localStorage.getItem('aria_cal_choice') || ''; } catch (_) { return ''; } });
  const [workEmbed, setWorkEmbed] = React.useState(() => { try { return localStorage.getItem('aria_work_cal_embed') || ''; } catch (_) { return ''; } });
  const [portalNote, setPortalNote] = React.useState('');
  // Once the calendar has been opened/connected, STAY connected forever — mount
  // the webview straight away on every future visit (no "Open my calendar" step).
  const [gcalOpen, setGcalOpen] = React.useState(() => { try { return localStorage.getItem('aria_gcal_open') === '1'; } catch (_) { return false; } });
  const openGcal = () => { try { localStorage.setItem('aria_gcal_open', '1'); } catch (_) {} setGcalOpen(true); };
  const chooseCal = (c) => { try { localStorage.setItem('aria_cal_choice', c); } catch (_) {} setCalChoice(c); };
  // "Aria gets to work": box slides top-right, the calendar shows a loading
  // state, then the sign-in (Connect) or the provisioned work calendar appears.
  const [calWorking, setCalWorking] = React.useState(null); // null | 'google' | 'work'
  const calWvRef = React.useRef(null);
  const calPortalRef = React.useRef(null);
  const calBannerShownRef = React.useRef(false);
  const [calCue, setCalCue] = React.useState(null); // { left, top, w, h, label } — pointer at the field to fill

  // The calendar captures the wheel ONLY when it's centered on screen (fills the
  // middle band of the viewport). Otherwise the overlay above scrolls the page.
  const [calScrollCaptured, setCalScrollCaptured] = React.useState(false);
  const _findScrollParent = (node) => {
    let el = node && node.parentElement;
    while (el) {
      const cs = getComputedStyle(el);
      if (/(auto|scroll)/.test(cs.overflowY + cs.overflow) && el.scrollHeight > el.clientHeight + 4) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };
  const onCalOverlayWheel = (e) => {
    const sc = _findScrollParent(calPortalRef.current);
    if (sc) { sc.scrollTop += e.deltaY; }
  };
  // Click the calendar while it's off-center → smoothly scroll it into the middle
  // of the screen so it becomes the focused, scrollable element.
  const centerCalendar = () => {
    const el = calPortalRef.current; if (!el) return;
    const sc = _findScrollParent(el);
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const delta = (r.top + r.height / 2) - vh / 2;
    try { sc.scrollTo({ top: sc.scrollTop + delta, behavior: 'smooth' }); }
    catch (_) { sc.scrollTop += delta; }
  };
  React.useEffect(() => {
    const update = () => {
      const el = calPortalRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // "in the middle of the screen": calendar spans the central vertical band.
      setCalScrollCaptured(r.top <= vh * 0.30 && r.bottom >= vh * 0.70);
    };
    update();
    const sc = _findScrollParent(calPortalRef.current);
    sc && sc.addEventListener('scroll', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    return () => { sc && sc.removeEventListener('scroll', update); window.removeEventListener('scroll', update, { capture: true }); window.removeEventListener('resize', update); };
  }, [calChoice, gcalOpen, portalMode]);

  // Assist walk-through of the Google sign-in: find the email/password field
  // INSIDE the webview, drop a glowing pointer + short caption on it, and follow
  // the user step to step (email → password → done). Desktop only (webview).
  React.useEffect(() => {
    if (!(isElectron && calChoice === 'google' && gcalOpen)) { setCalCue(null); return undefined; }
    let alive = true;
    const tick = async () => {
      const wv = calWvRef.current;
      if (!wv || !alive || typeof wv.executeJavaScript !== 'function') return;
      try {
        const info = await wv.executeJavaScript(`(function(){
          function pick(el,label){ if(!el) return null; var r=el.getBoundingClientRect(); if(r.width<20||r.top<0||r.left<0) return null; return {x:r.left,y:r.top,w:r.width,h:r.height,label:label}; }
          var field = pick(document.querySelector('input[type=email],#identifierId,input[name=identifier]'),'email')
                   || pick(document.querySelector('input[type=password],input[name=Passwd],input[name=password]'),'password')
                   || pick(document.querySelector('[data-identifier],[data-email],div[role=link][data-authuser]'),'account');
          var onCalendar = /calendar\\.google\\.com\\/calendar/.test(location.href);
          return { field: field, onCalendar: onCalendar, iw: window.innerWidth, ih: window.innerHeight };
        })()`).catch(() => null);
        if (!alive) return;
        if (info && info.field) {
          const r = wv.getBoundingClientRect();
          // Guest page pixels ≠ host pixels under display scaling / zoom — map by
          // the real ratio between the webview element and its guest viewport.
          const sx = info.iw ? r.width / info.iw : 1;
          const sy = info.ih ? r.height / info.ih : 1;
          const lbl = info.field.label === 'email' ? 'Type *your* email here, then Next'
                    : info.field.label === 'password' ? 'Now *your* password'
                    : 'Tap *your* account';
          setCalCue({ left: r.left + info.field.x * sx, top: r.top + info.field.y * sy, w: info.field.w * sx, h: info.field.h * sy, label: lbl });
        } else if (info && info.onCalendar) {
          setCalCue(null);
          // We reached the real calendar — remember it so it stays connected and
          // opens straight to the grid on every future visit (no reconnect).
          try { localStorage.setItem('aria_gcal_open', '1'); } catch (_) {}
          if (!calBannerShownRef.current) {
            calBannerShownRef.current = true;
            // Zoom the WHOLE calendar down so everything fits in the box — the full
            // week (Saturday included) AND Google's own top nav (the < > week
            // arrows). Do NOT hide Google's chrome: hiding the header removed the
            // week-navigation, and forcing overflow:hidden trapped Saturday
            // off-screen. Shrinking the whole thing is what "squeeze it into the
            // box" needs — everything visible + navigable.
            try { if (typeof wv.setZoomFactor === 'function') wv.setZoomFactor(0.8); } catch (_) {}
          }
        } else setCalCue(null);
      } catch (_) {}
    };
    const iv = setInterval(tick, 1000); tick();
    return () => { alive = false; clearInterval(iv); };
  }, [isElectron, calChoice, gcalOpen]);
  const onCalChoose = (id) => {
    setCalWorking(id);
    setPortalMode('cal');
    setTimeout(() => {
      if (id === 'google') { chooseCal('google'); setGcalOpen(true); } // deliberate connect → load the sign-in directly
      else makeWorkCalendar();
      setCalWorking(null);
    }, 1500);
  };
  const makeWorkCalendar = async () => {
    setPortalNote('Setting up your work calendar…');
    try {
      const d = await fetch(`${getApiBase()}/api/schedule?ownerId=${encodeURIComponent(ownerId)}`).then((r) => r.json());
      if (d && d.configured && d.embedUrl) {
        try { localStorage.setItem('aria_work_cal_embed', d.embedUrl); } catch (_) {}
        setWorkEmbed(d.embedUrl); chooseCal('work'); setPortalNote('');
      } else {
        setPortalNote('Almost — this needs the Google key from the Google Cloud setup. Tell Aria: "set up the google cloud apis" and this button works the moment it lands.');
      }
    } catch (_) { setPortalNote('Could not reach the scheduler — try again in a minute.'); }
  };
  // Per-agent setup state (which boxes are "set up" → blue) + the open detail.
  const [agentSetup, setAgentSetup] = React.useState(() => { try { return JSON.parse(localStorage.getItem('howto_agent_setup') || '{}'); } catch (_) { return {}; } });
  const [openAgentKey, setOpenAgentKey] = React.useState(null);
  // "Need some help?" support box that pops up over a non-scheduling agent when
  // it's opened (scheduling renders its own box inside SchedulingDetail).
  const [setupHelpFor, setSetupHelpFor] = React.useState(null);
  React.useEffect(() => {
    // 'expenses' is BOOKS now, and Books opens to the full automation list
    // (8/13). Auto-popping its "which accounts do you use?" checklist buried
    // that list behind a modal the moment the owner tapped in — he asked to
    // see what Aria runs, not a form. The checklist still opens from the
    // bank-connect button inside the panel.
    const SKIP_AUTO_HELP = new Set(['scheduling', 'expenses']);
    setSetupHelpFor((openAgentKey && !SKIP_AUTO_HELP.has(openAgentKey) && SETUP_FLOWS[openAgentKey]) ? openAgentKey : null);
  }, [openAgentKey]);
  const markSetup = (k, on = true) => setAgentSetup((prev) => { const next = { ...prev, [k]: on }; try { localStorage.setItem('howto_agent_setup', JSON.stringify(next)); } catch (_) {} return next; });
  // Scheduling's "set up" state is the REAL Google Calendar connection, not a flag.
  const [calConnected, setCalConnected] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    fetch(`${getApiBase()}/api/calendar/connected?userId=${encodeURIComponent(ownerId)}`)
      .then((r) => r.json()).then((d) => { if (alive) setCalConnected(!!(d && d.connected)); }).catch(() => {});
    return () => { alive = false; };
  }, [ownerId]);
  const [showCalSetup, setShowCalSetup] = React.useState(false);
  const agentIsSetup = (k) => (k === 'scheduling' ? calConnected : !!agentSetup[k]);
  // Middleman notification feed — the owner's inbox (badge = unread count).
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    fetch(`${getApiBase()}/api/notifications/unread-count?ownerId=${encodeURIComponent(ownerId)}`)
      .then((r) => r.json()).then((d) => { if (alive && d && d.ok) setUnread(d.count || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [ownerId]); // mount + owner only; the panel re-syncs the badge when it loads (no open/close refetch race)

  // Array (incl. []) → show exactly those; undefined → all. Matches the backend
  // operator (null = all on, [] = none).
  const enabledCaps = Array.isArray(agentConfig.enabledServices)
    ? AGENT_CAPS.filter((c) => agentConfig.enabledServices.includes(c.key))
    : AGENT_CAPS;
  const readyCount = enabledCaps.filter((c) => c.ready).length;

  const openSetup = () => {
    setBizType(agentConfig.businessType || '');
    setBizName(agentConfig.businessName || (project && project.name) || '');
    setPicked(agentConfig.enabledServices && agentConfig.enabledServices.length ? agentConfig.enabledServices : []);
    seededBizRef.current = agentConfig.businessType || '';
    setStep(1);
    setSetupOpen(true);
  };
  // Re-derive suggestions on first run (no picks) or when the business text
  // changed since picks were seeded — so editing the business updates the set.
  const nextFromBiz = () => {
    if (!picked.length || seededBizRef.current !== bizType) setPicked(suggestedKeys(bizType));
    seededBizRef.current = bizType;
    setStep(2);
  };
  const togglePick = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const activateAgent = () => {
    const cfg = { businessType: bizType.trim(), businessName: bizName.trim(), enabledServices: picked };
    setAgentConfig(cfg); setAgentActive(true);
    try {
      localStorage.setItem('howto_agent_active', '1');
      localStorage.setItem('howto_agent_runloc', runLocation);
      localStorage.setItem('howto_agent_config', JSON.stringify(cfg));
    } catch (_) {}
    setSetupOpen(false);
  };
  const pauseAgent = () => { setAgentActive(false); try { localStorage.setItem('howto_agent_active', '0'); } catch (_) {} };

  const name = (project && project.name) || (businessName && String(businessName).trim()) || (brand && brand.name) || 'Your business';
  // A "Generate business plan" project carries plan.{archetype, terminology, kpis, agents}.
  // When present it drives the dashboard (Jobs → Appointments / Users / Orders …) instead
  // of the local guesser; otherwise we classify from the project name/tag as before.
  const plan = (project && project.plan) || null;
  const arch = (plan && plan.archetype) || archetypeOf(project && (project.tag || project.kind || project.category), project && project.name, businessName);
  const top = TOP[arch] || TOP.default;
  // The agent set Aria built for THIS business — filters the board so a SaaS shows no
  // licensing/quotes, a clinic shows no quotes, etc. No plan ⇒ show all (trades default).
  const planAgentKeys = (plan && Array.isArray(plan.agents) && plan.agents.length) ? new Set(plan.agents) : null;
  // Release-channel gating: on 'release' only the manifest's shipped automations
  // show. Dev channel (Electron or aria_channel='dev') never gates anything.
  const channel = getChannel();
  const [releaseAutomations, setReleaseAutomations] = React.useState(null);
  React.useEffect(() => {
    if (channel !== 'release') return undefined;
    let alive = true;
    loadManifest().then((m) => { if (alive) setReleaseAutomations((m && m.automations) || null); });
    return () => { alive = false; };
  }, [channel]);
  // Manifest unreachable (null) → fail open so a network blip never blanks the board.
  const releasedAgent = (key) => channel !== 'release' || !releaseAutomations || !!releaseAutomations[AGENT_RELEASE_KEY[key]];
  const showAgent = (key) => (!planAgentKeys || planAgentKeys.has(key)) && releasedAgent(key);
  const periodLabel = PERIOD_LABEL[period];

  // Pull real money for this owner + period; refetch when either changes.
  React.useEffect(() => {
    let alive = true;
    const base = getApiBase();
    fetch(`${base}/api/company/financials?ownerId=${encodeURIComponent(ownerId)}&period=${period}`)
      .then((r) => r.json())
      .then((d) => { if (alive && d && d.ok) setFin(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ownerId, period]);

  const money = (n) => (n == null || !Number.isFinite(Number(n))) ? '—' : '$' + Math.round(Number(n)).toLocaleString('en-US');
  const sum = fin && fin.summary;
  const isTrades = arch === 'trades' || arch === 'default';
  // Headline KPIs — for a trades/owner business these are the real money numbers.
  const topMetrics = isTrades
    ? [
        { label: 'Jobs done', value: sum ? String(sum.jobsCompleted) : '—', tone: 'neutral', drill: 'gross' },
        { label: 'Unpaid / overdue', value: sum ? money(sum.unpaid) : '—', tone: 'warn', drill: 'unpaid' },
      ]
    // Non-trades archetypes have no live source for these KPIs yet, so they
    // show the label and a dash — the same thing money() does for trades when
    // financials are empty. Never a stand-in number (8/22).
    : top.map((m) => ({ ...m, value: '—', drill: null }));
  // Owner call 2026-07-11: the hand-picked money row IS the dashboard — the
  // generated archetype tiles duplicated Revenue and added noise (Avg job
  // value, New leads). Generated layout styles the page; it does NOT swap KPIs.
  const displayTop = topMetrics;
  // Money row — Gross (jobs completed) · Expenses · Net. ONLY Net is a "+"; only
  // Expenses is a "−". Gross/Revenue are plain totals (no sign), per the owner.
  const bottomMetrics = [
    { base: 'Revenue', value: sum ? money(sum.revenue) : '—', tone: 'good', drill: 'revenue' },
    { base: 'Expenses', value: sum ? money(sum.expenses) : '—', tone: 'bad', sign: '−', drill: 'expenses' },
    { base: 'Net', value: sum ? money(sum.net) : '—', tone: 'good', sign: '+', drill: 'net' },
  ];

  const openAgent = (agent) => {
    if (!agent.go) return;
    if (agent.ext) {
      try {
        if (window.electronAPI && window.electronAPI.openExternal) window.electronAPI.openExternal(agent.go);
        else window.open(agent.go, '_blank');
      } catch (_) { try { window.open(agent.go, '_blank'); } catch (__) { /* ignore */ } }
    } else if (onNavigate) {
      onNavigate(agent.go);
    }
  };

  const ariaCount = AGENT_GROUPS.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className={`ceo${theme === 'day' ? ' theme-light' : ''}${calChoice ? '' : ' ceo--precal'}`}>
      <style>{STYLE}</style>
      <div className="wrap">

        <div className="ceo-hero ceo-hero--brand">
          {(() => {
            // Generated layout wins; brand scrape is the fallback. No photos →
            // honest brand-gradient banner + logo watermark (never stock/AI).
            const bannerPhotos = (layout && layout.banner && layout.banner.photos) || (brand && brand.photos) || [];
            if (layout && layout.bannerMode === 'gradient') {
              return (
                <div className="ceo-banner ceo-banner--gradient" aria-hidden>
                  {layout.banner && layout.banner.watermark && <img className="ceo-banner__wm" src={layout.banner.watermark} alt="" />}
                </div>
              );
            }
            return bannerPhotos.length > 0 ? (
              <div className="ceo-banner" aria-hidden>
                {bannerPhotos.slice(0, 3).map((u, i) => <img key={i} src={u} alt="" />)}
              </div>
            ) : null;
          })()}
          <div className="ceo-banner-spacer" />
          {(brand || layout) && (
            <div className="ceo-brandcard">
              <div className="ceo-bizname">{name}</div>
            </div>
          )}
          {/* the round logo badge — half over the banner's bottom edge, centered */}
          {(() => {
            const badgeUrl = (layout && layout.badge && layout.badge.url) || (brand && brand.logo) || '';
            return badgeUrl ? <img className="ceo-avatar" src={badgeUrl} alt="" /> : null;
          })()}
        </div>

        <div className="ceo-head">
          <div className="ceo-toggles">
            {/* Day/Night moved to the global app header (themes the whole app) */}
            <div className="range">
              {['week', 'month', 'year'].map((p) => (
                <button key={p} type="button" className={period === p ? 'on' : ''} onClick={() => setPeriod(p)}>
                  {p[0].toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {layout && Array.isArray(layout.photoWall) ? (
          // Generated wall: right-first alternating cascade, no-overlap math
          // baked into the config. Empty ([]) = no photos yet → wall hides
          // entirely (empty edges beat fake photos — owner rule).
          layout.photoWall.length > 0 && (
            <div className="ceo-brandbg" aria-hidden>
              {layout.photoWall.map((p, i2) => (
                <span key={i2} className="ceo-polaroid"
                  style={{ [p.side === 'left' ? 'left' : 'right']: '10px', top: p.topVh + 'vh', transform: `rotate(${p.rotate}deg)`, maxWidth: 'calc(var(--gutter) - 58px)' }}>
                  <i className="tp tp1" /><i className="tp tp2" /><i className="tp tp3" /><i className="tp tp4" /><img src={p.src} alt="" />
                </span>
              ))}
            </div>
          )
        ) : (brand && (brand.photos || []).length > 0 && (
          <div className="ceo-brandbg" aria-hidden>
            {brand.photos.slice(0, 6).map((u, i2) => (
              <span key={i2} className={'ceo-polaroid pp' + i2}><i className="tp tp1" /><i className="tp tp2" /><i className="tp tp3" /><i className="tp tp4" /><img src={u} alt="" /></span>
            ))}
          </div>
        ))}
        

        {/* Section order comes from the generated layout when present
            (layout.sections); components themselves are untouched. */}
        {(() => {
        const SECTIONS = {};
        SECTIONS.stats = (
        <React.Fragment key="stats">
        <div className="ceo-kicker today-big-title">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        <div className="ceo-grid" style={{ gridTemplateColumns: `repeat(${displayTop.length}, minmax(0,1fr))` }}>
          {displayTop.map((m) => (
            <Bubble key={m.label} label={m.label} value={m.value} tone={m.tone} sign={m.sign}
              onClick={m.drill ? () => setDrill(m.drill) : undefined} />
          ))}
        </div>
        <div className="ceo-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
          {bottomMetrics.map((m) => (
            <Bubble key={m.base} label={`${m.base} · ${periodLabel}`} value={m.value} tone={m.tone} sign={m.sign}
              onClick={() => setDrill(m.drill)} />
          ))}
        </div>
        </React.Fragment>
        );
        SECTIONS.schedule = (
        <React.Fragment key="schedule">
        {/* "+ New Job" pulled off the company page (Braeden 8/13). Jobs get
            added from the Jobs board, or by just telling Aria — a button above
            the schedule was a third way to do the same thing. NewJobButton
            still exists and is still used on the home screen. */}
        {/* THE CALENDAR PORTAL — a clean window: connect once, then your week */}
        <div className="ceo-cal-portal" ref={calPortalRef}>
          {/* Scroll passthrough: the calendar only captures the wheel when it's
              centered on screen. Otherwise this overlay swallows the wheel and
              scrolls the PAGE instead, so you're never trapped scrolling inside
              the calendar when you meant to scroll past it. */}
          {portalMode !== 'map' && (
            <div
              onWheel={onCalOverlayWheel}
              onClick={centerCalendar}
              title="Click to focus the calendar"
              style={{ position: 'absolute', left: 0, right: 0, top: 48, bottom: 0, zIndex: 3, background: 'transparent', cursor: 'pointer', pointerEvents: calScrollCaptured ? 'none' : 'auto' }}
              aria-hidden
            />
          )}
          <div className="ceo-cal-portal__bar">
            <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ display: 'flex', gap: 9, fontSize: 11, color: '#5f6368', alignItems: 'center' }}>
                {[['#188038', 'Confirmed'], ['#f4b400', 'Set'], ['#d93025', 'Canceled']].map(([c, l]) => (
                  <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />{l}
                  </span>
                ))}
              </span>
            </span>
            <span className="ceo-cal-portal__title">Schedule</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {[['cal', 'Calendar'], ['map', 'Map']].map(([k, lbl]) => (
                <button key={k} type="button" className="agent2__morelink"
                  style={portalMode === k ? { background: '#1a73e8', color: '#fff', borderRadius: 14, padding: '4px 12px' } : { padding: '4px 12px' }}
                  onClick={() => setPortalMode(k)}>{lbl}</button>
              ))}
            </span>
          </div>
          {calWorking && (
            <div style={{ position: 'absolute', inset: 0, top: 46, zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: theme === 'day' ? 'rgba(255,255,255,.96)' : 'rgba(19,23,34,.97)', color: theme === 'day' ? '#1f2937' : '#e8eaed' }}>
              <div className="ceo-cal-spin" />
              <div style={{ fontSize: 15, fontWeight: 700 }}>{calWorking === 'google' ? 'Opening your calendar sign-in…' : 'Setting up your work calendar…'}</div>
              <div style={{ fontSize: 12.5, color: '#5f6368' }}>Aria's on it.</div>
            </div>
          )}
          {calCue && _createPortal(
            <div style={{ position: 'fixed', left: calCue.left - 12, top: calCue.top - 12, width: calCue.w + 24, height: calCue.h + 24, zIndex: 2147483000, pointerEvents: 'none' }}>
              <div className="cal-cue-ring" />
              <div className="cal-cue-arrow">▲</div>
              <div className="cal-cue-cap">{renderEmph(calCue.label)}</div>
            </div>, document.body)}
          {portalMode === 'map' ? (
            <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
              <iframe title="job map" style={{ width: '100%', height: '100%', border: 0 }}
                src={myLoc
                  ? `https://maps.google.com/maps?q=${myLoc.lat},${myLoc.lng}&z=13&output=embed`
                  : 'https://maps.google.com/maps?q=' + encodeURIComponent('Wichita KS') + '&z=11&output=embed'} />
              {/* Same as the calendar: the map only grabs the wheel (zoom) when it's
                  centered on screen. Off-center, this overlay scrolls the page; a
                  click centers the map so it becomes the zoom target. */}
              <div
                onWheel={onCalOverlayWheel}
                onClick={centerCalendar}
                title="Click to focus the map"
                style={{ position: 'absolute', inset: 0, zIndex: 3, background: 'transparent', cursor: 'pointer', pointerEvents: calScrollCaptured ? 'none' : 'auto' }}
                aria-hidden
              />
              {!myLoc ? (
                <div style={{ position: 'absolute', left: '50%', bottom: 22, transform: 'translateX(-50%)', zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, background: 'rgba(20,24,34,.92)', color: '#fff', padding: '14px 20px', borderRadius: 16, boxShadow: '0 8px 26px rgba(0,0,0,.4)', textAlign: 'center', maxWidth: 420 }}>
                  <button type="button" onClick={requestLocation} disabled={locStatus === 'asking'}
                    style={{ fontSize: 15, fontWeight: 700, background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 22px', cursor: 'pointer' }}>
                    {locStatus === 'asking' ? 'Getting your location…' : '📍 Use my location'}
                  </button>
                  <div style={{ fontSize: 12.5, opacity: .85 }}>
                    {locStatus === 'denied' ? 'Location is off — turn it on for HowTo in your browser/Windows settings, then try again.'
                      : locStatus === 'error' ? "Couldn't get your location. Check that location services are on."
                      : 'Lets Aria route jobs from where you are and see what’s nearby.'}
                  </div>
                </div>
              ) : (
                <button type="button" onClick={requestLocation} title="Recenter on my location"
                  style={{ position: 'absolute', top: 12, right: 12, zIndex: 4, fontSize: 13, fontWeight: 700, background: 'rgba(20,24,34,.92)', color: '#fff', border: 'none', borderRadius: 999, padding: '8px 14px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,.35)' }}>
                  📍 My location
                </button>
              )}
            </div>
          ) : !calChoice ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center', background: theme === 'day' ? '#fff' : '#131722', color: theme === 'day' ? '#1f2937' : '#e8eaed' }}>
              <button type="button" onClick={() => setSetupHelpFor('calendar')}
                style={{ fontSize: 15, fontWeight: 700, background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 24, padding: '13px 34px', cursor: 'pointer' }}>
                Get started
              </button>
              {portalNote && <div style={{ fontSize: 12.5, color: '#b45309', maxWidth: 430 }}>{portalNote}</div>}
            </div>
          ) : calChoice === 'work' && workEmbed ? (
            <iframe title="work calendar" style={{ width: '100%', height: '100%', border: 0 }} src={workEmbed} />
          ) : isElectron && !gcalOpen ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: theme === 'day' ? '#fff' : '#131722', color: theme === 'day' ? '#1f2937' : '#e8eaed' }}>
              <button type="button" onClick={openGcal}
                style={{ fontSize: 15, fontWeight: 700, background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 24, padding: '13px 30px', cursor: 'pointer' }}>
                Open my Google Calendar
              </button>
            </div>
          ) : isElectron ? (
            <webview src={(() => {
                const em = (typeof localStorage !== 'undefined' && (localStorage.getItem('aria_session_email') || localStorage.getItem('aria_user_email'))) || (brand && brand.ownerEmail) || '';
                const cont = 'https%3A%2F%2Fcalendar.google.com%2Fcalendar%2Fu%2F0%2Fr%2Fweek';
                return em
                  ? 'https://accounts.google.com/AccountChooser?Email=' + encodeURIComponent(em) + '&continue=' + cont
                  : 'https://accounts.google.com/ServiceLogin?service=cl&continue=' + cont;
              })()} partition="persist:gcal"
              ref={calWvRef}
              useragent="Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:127.0) Gecko/20100101 Firefox/127.0"
              style={{ width: '100%', height: '100%', border: 0, display: 'inline-flex' }} />
          ) : (
            <iframe title="google calendar" style={{ width: '100%', height: '100%', border: 0 }}
              src={'https://calendar.google.com/calendar/embed?mode=WEEK&wkst=1&showPrint=0&showCalendars=0&src=' + encodeURIComponent((typeof localStorage !== 'undefined' && (localStorage.getItem('aria_gcal_embed_src') || localStorage.getItem('aria_session_email'))) || '')} />
          )}
        </div>
        </React.Fragment>
        );
        SECTIONS.software = (
        <React.Fragment key="software">
        {/* ceo-today--software: this section is pinned BLUE (owner spec 2026-07-14),
            not the business brand accent (HowTo's gold made it yellow). */}
        <div className="ceo-today ceo-today--software">
          {/* Braeden 8/10: "no more set up agent, just the normal list ... needs to
              be the WHOLE list of what she does including leads, including jobs."
              So: one flat list of every agent, no set-up/active split, no
              "Set up agent" button, and rows that own a real page go straight
              there (jobs, leads, invoices, books) instead of opening a panel —
              that's how a new job gets added from the website. */}
          <div className="today-big-title">What Aria does</div>

          <div className="agent-tasks">
            <div className="task-list biglist">
              {BIG_ROWS.map((r) => (
                <button
                  type="button"
                  key={r.key}
                  className="bigrow"
                  onClick={() => {
                    // The row ALWAYS opens the thing. Gating it behind setup
                    // questions was wrong — clicking Books has to show the
                    // books (8/13). Setup is offered on the page itself, and
                    // by the "Set up" chip on this row, never instead of it.
                    // Website opens the owner's REAL site in a new tab (spec 8/10).
                    if (r.key === 'website') {
                      const raw = project?.website || project?.site || project?.domain || '';
                      if (raw) { const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`; window.open(u, '_blank', 'noopener,noreferrer'); return; }
                    }
                    // Books and Systems are both full pages now — no overlay.
                    if (r.section && onNavigate) onNavigate(r.section);
                    else setOpenAgentKey(r.agent || null);
                  }}
                >
                  <span className="bigrow__label">{r.label}</span>
                  <span className="bigrow__detail">
                    {r.key === 'website'
                      ? (project?.website || project?.site || project?.domain || r.detail)
                      : r.detail}
                  </span>
                  <span className="bigrow__go">
                    {onboard[r.key] && !onboard[r.key].ready && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="bigrow__setup"
                        title={`${onboard[r.key].remaining} still to tell me`}
                        onClick={(e) => { e.stopPropagation(); setOnboardKey(r.key); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setOnboardKey(r.key); } }}
                      >Set up · {onboard[r.key].remaining}</span>
                    )}
                    {r.action} &rarr;
                  </span>
                </button>
              ))}
            </div>

            {/* The full automation catalogue used to render here. Braeden 8/13:
                "everything you added needs to be inside books, so when you click
                into books, thats what pops up" — so it moved to ExpensesPanel
                (the Books row above opens it) and this page keeps its five
                navigation rows. One home for it, not two. */}
          </div>
        </div>

        </React.Fragment>
        );
        // ── SYSTEM (Braeden 8/13) ──────────────────────────────────────────
        // "add a new tab in the business profile, says system and thats where
        // everything in books needs to go, than books needs to be a records of
        // everything in the business litterealy everything."
        // The Books: jobs, quotes, invoices, payments, expenses, payroll,
        // leads and every action Aria took — one timeline, newest first,
        // straight off the live records so it can't drift.
        // Braeden 8/13: "get rid of the thing on the bottom" + "everything in
        // books needs to be in systems" — the record is no longer a section
        // stacked under the page. It opens from the Systems row, full screen.
        SECTIONS.vision = (
        <React.Fragment key="vision">
        {/* VISION BOARD — why you're doing this (goal + progress toward it) */}
        <VisionBoard ownerId={ownerId} brand={brand} />
        </React.Fragment>
        );
        // Braeden 8/10: vision board ("Why you're doing this") pulled off the page.
        // Component + SECTIONS.vision stay defined so it can be dropped back into
        // this array later; it just isn't rendered.
        const DEFAULT_ORDER = ['stats', 'schedule', 'software'];
        // Owner rule: below-schedule sections stay hidden until the calendar
        // is connected or built - the schedule is step one of the page.
        const calReady = !!calChoice;
        const order = (layout && Array.isArray(layout.sections) && layout.sections.length
          ? layout.sections.filter((k) => SECTIONS[k])
          // The books are a RECORD, not a feature — they show whether or not
          // the calendar is connected (8/13). Only software/vision wait.
          : DEFAULT_ORDER).filter((k) => calReady || (k !== 'software' && k !== 'vision'));
        const missing = DEFAULT_ORDER.filter((k) => !order.includes(k) && (calReady || (k !== 'software' && k !== 'vision')));
        return [...order, ...missing].map((k) => SECTIONS[k]);
        })()}

        {/* the Systems overlay was retired 8/13 — Systems is its own page now,
            and the record it used to show is the Books page. */}

        {/* ONBOARDING — one question at a time, opened from the row itself */}
        {onboardKey && (
          <OnboardingView
            surfaceKey={onboardKey}
            theme={theme}
            onClose={() => { setOnboardKey(null); loadOnboarding(); }}
            onReady={() => loadOnboarding()}
          />
        )}

        {setupOpen && (
          <div className="agent-setup" onClick={(e) => { if (e.currentTarget === e.target) setSetupOpen(false); }}>
            <div className="agent-setup__panel">
              <button type="button" className="agent-setup__close" onClick={() => setSetupOpen(false)} aria-label="Close">×</button>
              <div className="agent-setup__steps">
                {[1, 2, 3].map((n) => <span key={n} className={`agent-setup__step${step === n ? ' on' : ''}${step > n ? ' done' : ''}`} />)}
              </div>

              {step === 1 && (
                <div className="agent-setup__body">
                  <div className="agent-setup__title">Let's set up your agent</div>
                  <div className="agent-setup__lead">First — tell me a bit about the business so I switch on the right jobs.</div>
                  <label className="agent-field">
                    <span className="agent-field__label">What does your business do?</span>
                    <input className="agent-field__input" value={bizType} autoFocus placeholder="e.g. HVAC repair, junk removal, dental clinic" onChange={(e) => setBizType(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && bizType.trim()) nextFromBiz(); }} />
                  </label>
                  <label className="agent-field">
                    <span className="agent-field__label">What's it called?</span>
                    <input className="agent-field__input" value={bizName} placeholder="Business name" onChange={(e) => setBizName(e.target.value)} />
                  </label>
                  <div className="agent-setup__foot">
                    <span />
                    <button type="button" className="agent-setup__activate agent-setup__next" disabled={!bizType.trim()} onClick={nextFromBiz}>Next</button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="agent-setup__body">
                  <div className="agent-setup__title">Which jobs should I handle?</div>
                  <div className="agent-setup__lead">For {bizType.trim() ? `"${bizType.trim()}"` : 'your business'}, I'd suggest these. Tap to add or remove — you can change this anytime.</div>
                  {/* GROUPED BY HOW OFTEN IT COMES AROUND (8/13). A flat list
                      was fine at four items; at twenty-seven it is a wall. An
                      owner thinks in "every day / every job / every year", so
                      the headings match that and nothing important is buried. */}
                  <div className="agent-setup__caps">
                    {CAP_GROUP_ORDER.map((g) => {
                      const inGroup = AGENT_CAPS.filter((c) => (c.group || 'Every day') === g);
                      if (!inGroup.length) return null;
                      return (
                        <React.Fragment key={g}>
                          <div className="agent-pick__group">{g}</div>
                          {inGroup.map((c) => {
                            const on = picked.includes(c.key);
                            const rec = suggestedKeys(bizType).includes(c.key);
                            return (
                              <button type="button" key={c.key} className={`agent-pick${on ? ' on' : ''}`} onClick={() => togglePick(c.key)}>
                                <span className={`agent-pick__check${on ? ' on' : ''}`} aria-hidden>{on ? '✓' : ''}</span>
                                <span className="agent-pick__main">
                                  <span className="agent-pick__name">{c.label}{rec && <span className="agent-pick__rec">Suggested</span>}</span>
                                  <span className="agent-pick__detail">{c.detail}</span>
                                </span>
                                <span className={`agent-cap__status${c.ready ? ' ready' : ''}`}>{c.ready ? 'Ready' : c.needs}</span>
                              </button>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="agent-setup__foot">
                    <button type="button" className="agent-setup__back" onClick={() => setStep(1)}>Back</button>
                    <button type="button" className="agent-setup__activate agent-setup__next" disabled={!picked.length} onClick={() => setStep(3)}>Next</button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="agent-setup__body">
                  <div className="agent-setup__title">Last thing — where should I run?</div>
                  <div className="agent-setup__lead">I'll handle {picked.length} job{picked.length === 1 ? '' : 's'} for {bizName.trim() || 'your business'}, around the clock.</div>
                  <div className="agent-setup__runloc">
                    <button type="button" className={`runloc${runLocation === 'pc' ? ' on' : ''}`} onClick={() => setRunLocation('pc')}>
                      <span className="runloc__title">This computer</span>
                      <span className="runloc__sub">Free · must stay on & online</span>
                    </button>
                    <button type="button" className={`runloc${runLocation === 'cloud' ? ' on' : ''}`} onClick={() => setRunLocation('cloud')}>
                      <span className="runloc__title">Cloud · always-on</span>
                      <span className="runloc__sub">Reliable 24/7 · ~$5–10/mo</span>
                    </button>
                  </div>
                  <div className="agent-setup__review">
                    {AGENT_CAPS.filter((c) => picked.includes(c.key)).map((c) => (
                      <div key={c.key} className={`agent-cap${c.ready ? ' ready' : ''}`}>
                        <span className="agent-cap__dot" aria-hidden />
                        <span className="agent-cap__name">{c.label}</span>
                        <span className="agent-cap__detail">{c.detail}</span>
                        <span className="agent-cap__status">{c.ready ? 'Ready' : c.needs}</span>
                      </div>
                    ))}
                  </div>
                  <div className="agent-setup__foot">
                    <button type="button" className="agent-setup__back" onClick={() => setStep(2)}>Back</button>
                    <button type="button" className="agent-setup__activate" onClick={activateAgent}>Activate agent</button>
                  </div>
                  <div className="agent-setup__note">Jobs marked "needs setup" go live the moment their key is added{runLocation === 'pc' ? '. On this computer, inbound texts also need a free tunnel.' : '.'}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {drill && fin && (
          <DrillDown metric={drill} fin={fin} periodLabel={periodLabel} onClose={() => setDrill(null)} />
        )}

        {openAgentKey && (() => {
          const a = AGENTS.find((x) => x.key === openAgentKey);
          if (!a) return null;
          if (a.key === 'scheduling') {
            return (<>
              <ScheduleBoard ownerId={ownerId} onClose={() => setOpenAgentKey(null)} onOpenSetup={() => setShowCalSetup(true)} />
              {showCalSetup && <SchedulingDetail ownerId={ownerId} onClose={() => setShowCalSetup(false)} onConnectedChange={setCalConnected} />}
            </>);
          }
          const Panel = AGENT_PANELS[a.key];
          if (Panel) return <Panel ownerId={ownerId} onClose={() => setOpenAgentKey(null)} />;
          return <AgentDetail agent={a} setup={agentIsSetup(a.key)} onClose={() => setOpenAgentKey(null)} onActivate={(k) => markSetup(k, true)} />;
        })()}

        {/* "Need some help?" support box on Set up — non-scheduling agents */}
        {setupHelpFor && SETUP_FLOWS[setupHelpFor] && (
          <SetupHelpBox
            open
            onClose={() => {
              if (setupHelpFor === 'calendar') {
                try {
                  const c = localStorage.getItem('howto_setup_calendar_choice');
                  if (c === 'google') chooseCal('google');
                  else if (c === 'work') makeWorkCalendar();
                } catch (_) {}
              }
              setSetupHelpFor(null);
            }}
            agentTitle={(AGENTS.find((x) => x.key === setupHelpFor) || {}).title || ''}
            ownerId={ownerId}
            onDone={() => { if (setupHelpFor && setupHelpFor !== 'calendar') markSetup(setupHelpFor, true); }}
            corner={setupHelpFor === 'calendar'}
            onChoose={setupHelpFor === 'calendar' ? onCalChoose : null}
            pinTopRight={setupHelpFor === 'calendar' && !!calWorking}
            greeting={SETUP_FLOWS[setupHelpFor].greeting}
            checklist={SETUP_FLOWS[setupHelpFor].checklist}
            preChoice={SETUP_FLOWS[setupHelpFor].preChoice}
            previewNode={setupHelpFor === 'invoicing' ? <InvoicePreview /> : null}
          />
        )}

        {notifOpen && (
          <NotificationsPanel ownerId={ownerId} onClose={() => setNotifOpen(false)} onUnreadChange={setUnread} />
        )}

      </div>
    </div>
  );
}
